/**
 * 内容编辑页面 - 完整标书预览和生成
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { fmtDateTimeCST } from '../utils/datetime';
import { OutlineData, OutlineItem, OutlineSource, OUTLINE_SOURCE_LABELS, GenerationMode, AnalysisResultMap } from '../types';
import { DocumentTextIcon, PlayIcon, DocumentArrowDownIcon, CheckCircleIcon, ExclamationCircleIcon, ArrowUpIcon, ArrowPathIcon, CodeBracketIcon, PencilSquareIcon, ClockIcon, QueueListIcon, ArchiveBoxIcon, XMarkIcon, TrashIcon, ChevronDownIcon, ArrowsPointingOutIcon, SparklesIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { collectSseText, contentApi, ChapterContentRequest, documentApi, getErrorMessage, projectApi, BatchGenerateRequest, GenerateStatusResponse, ExportTaskInfo, ProjectBatchTaskResponse } from '../services/api';
import { saveAs } from 'file-saver';
import { createDraftStorage } from '../utils/draftStorage';
import { useAuth } from '../hooks/useAuth';
import { hasFeature } from '../utils/license';
import ConfirmDialog from '../components/ConfirmDialog';
import MarkdownRenderer from '../components/MarkdownRenderer';
import VersionManager from '../components/VersionManager';
import FullscreenEditor from '../components/FullscreenEditor';
import WordExportDialog from '../components/WordExportDialog';
import OutlineEditorDialog from '../components/OutlineEditorDialog';
import ContentEditMermaidPanel from '../components/ContentEditMermaidPanel';
import ContentEditImageCheckPanel from '../components/ContentEditImageCheckPanel';
import ContentEditTaskPanel from '../components/ContentEditTaskPanel';
import { useContentDragDrop } from '../hooks/useContentDragDrop';
import { useContentMermaid } from '../hooks/useContentMermaid';
import { useContentImageCheck } from '../hooks/useContentImageCheck';
import mermaid from 'mermaid';
import { preprocessMermaidCode, getGanttAfterMismatches, escapeRegex } from '../utils/mermaidPreprocessor';

interface ContentEditProps {
  outlineData: OutlineData | null;
  bizOutlineData?: OutlineData | null;
  projectId?: number;
  onOutlineUpdated?: (outlineData: OutlineData) => void;
  onBizOutlineUpdated?: (bizOutlineData: OutlineData | null) => void;
  onProjectRefresh?: () => void;
  onProjectEditing?: () => void;
  concurrency?: number;
  mermaidDefaultView?: 'code' | 'chart';
  projectName?: string;
  companyName?: string;
  techRequirements?: string;
  generationMode?: GenerationMode;
  analysisResults?: AnalysisResultMap;
  outlineSource?: OutlineSource | null;
  readOnly?: boolean;
}

interface GenerationProgress {
  total: number;
  completed: number;
  current: string;
  failed: string[];
  generating: Set<string>; // 正在生成的项目ID集合
}


const ContentEdit: React.FC<ContentEditProps> = ({
  outlineData,
  bizOutlineData = null,
  projectId,
  onOutlineUpdated,
  onBizOutlineUpdated,
  onProjectRefresh,
  onProjectEditing,
  concurrency = 2,
  mermaidDefaultView = 'code',
  projectName,
  companyName,
  techRequirements = '',
  generationMode = 'tech',
  analysisResults = {},
  outlineSource = null,
  readOnly = false,
}) => {
  const { user, license } = useAuth();
  const isAdmin = user?.role === 'admin';
  const draftStore = useMemo(() => createDraftStorage(user?.id ?? 0), [user?.id]);

  // 商务部分 / 技术部分切换（仅整套投标文件模式时生效）
  // 必须早于 effectiveOutlineData 和 dragDrop hook 声明
  const [activeSection, setActiveSection] = useState<'tech' | 'biz'>('tech');

  // 当前有效的目录数据（提前定义，供 dragDrop hook 使用）
  const effectiveOutlineData = useMemo(() => {
    if (generationMode === 'full' && activeSection === 'biz') {
      return bizOutlineData;
    }
    return outlineData;
  }, [generationMode, activeSection, bizOutlineData, outlineData]);

  // 目录树拖拽排序 hook（使用当前生效的 outline 数据和对应的回调）
  const dragDrop = useContentDragDrop({
    outlineData: effectiveOutlineData,
    projectId,
    onOutlineUpdated: ((data) => {
      if (generationMode === 'full' && activeSection === 'biz') {
        onBizOutlineUpdated?.(data);
      } else {
        onOutlineUpdated?.(data);
      }
    }) as (data: OutlineData) => void,
    section: generationMode === 'full' ? activeSection : 'tech',
  });

  // 终止生成的 AbortController 引用
  const abortControllerRef = useRef<AbortController | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [progress, setProgress] = useState<GenerationProgress>({
    total: 0,
    completed: 0,
    current: '',
    failed: [],
    generating: new Set<string>()
  });
  const [leafItems, setLeafItems] = useState<OutlineItem[]>([]);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [showPromptDialog, setShowPromptDialog] = useState(false);
  const [showOutlineSidebar, setShowOutlineSidebar] = useState(true);
  const [sidebarWidth, setSidebarWidth] = useState(288);
  const [showClearContentDialog, setShowClearContentDialog] = useState(false);
  const [showClearNodeContentDialog, setShowClearNodeContentDialog] = useState(false);
  const [clearNodeTarget, setClearNodeTarget] = useState<OutlineItem | null>(null);
  const [showOutlineEditor, setShowOutlineEditor] = useState(false);
  const [clearingContent, setClearingContent] = useState(false);
  const isResizingRef = useRef(false);
  const resizeStartXRef = useRef(0);
  const resizeStartWidthRef = useRef(0);
  const [isResizingSidebar, setIsResizingSidebar] = useState(false);
  const [showVersionManager, setShowVersionManager] = useState(false);


  // Word 导出模板选择对话框
  const [showExportDialog, setShowExportDialog] = useState(false);
  // 标记当前对话框是否用于"导出完整 Word"（合并商务+技术）
  const fullExportModeRef = useRef(false);
  // 全屏编辑器状态（替代原来的左侧目录树行内编辑）
  const [fullscreenEditorItem, setFullscreenEditorItem] = useState<OutlineItem | null>(null);
  // 编辑描述和数字范围的状态
  const [editingMetaId, setEditingMetaId] = useState<string | null>(null);
  const [editingMetaTitle, setEditingMetaTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editWordCountRange, setEditWordCountRange] = useState('');
  const [aiGeneratingDesc, setAiGeneratingDesc] = useState(false);
  // 行内编辑标题的状态
  const [editingTitleId, setEditingTitleId] = useState<string | null>(null);
  const [editTitleText, setEditTitleText] = useState('');
  const titleInputRef = useRef<HTMLInputElement>(null);
  // 右侧正文区域直接编辑的状态
  const [editingContentId, setEditingContentId] = useState<string | null>(null);
  const [editContentDirect, setEditContentDirect] = useState('');

  // 商务部分叶子节点（独立维护）
  const [bizLeafItems, setBizLeafItems] = useState<OutlineItem[]>([]);

  // 后台生成模式
  const [generateMode, setGenerateMode] = useState<'foreground' | 'background'>('background');
  const [showModeSelect, setShowModeSelect] = useState(false);
  // 导出下拉菜单
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [bgTaskId, setBgTaskId] = useState<number | null>(null);
  const [bgPollingRef] = useState<{ current: Record<string, ReturnType<typeof setInterval> | null> }>({ current: {} });
  // Word 导出后台任务（任务列表模式）
  const [showTaskList, setShowTaskList] = useState(false);
  const [taskList, setTaskList] = useState<ExportTaskInfo[]>([]);
  const [taskListPollingRef] = useState<{ current: ReturnType<typeof setInterval> | null }>({ current: null });


  // 右侧正文滚动时高亮左侧目录对应章节
  const [highlightedItemId, setHighlightedItemId] = useState<string | null>(null);
  const contentScrollRef = useRef<HTMLDivElement | null>(null);
  const sidebarScrollRef = useRef<HTMLDivElement | null>(null);
  // 手动滚动标志：点击章节时设为 true，阻止 IntersectionObserver 反馈导致侧边栏二次滚动
  const isManualScrollingRef = useRef(false);
  const manualScrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 收集所有叶子节点
  const collectLeafItems = useCallback((items: OutlineItem[]): OutlineItem[] => {
    let leaves: OutlineItem[] = [];
    items.forEach(item => {
      if (!item.children || item.children.length === 0) {
        leaves.push(item);
      } else {
        leaves = leaves.concat(collectLeafItems(item.children));
      }
    });
    return leaves;
  }, []);

  // 当前有效的叶子节点（使用 state 中的项以保留已生成的内容）
  const effectiveLeafItems = useMemo(() => {
    if (generationMode === 'full' && activeSection === 'biz') {
      return bizLeafItems;
    }
    return leafItems;
  }, [generationMode, activeSection, bizLeafItems, leafItems]);

  // Mermaid 检查/修复 hook
  const mermaidHook = useContentMermaid({
    effectiveLeafItems,
    projectId,
    draftStore,
    generationMode,
    activeSection,
    setBizLeafItems,
    setLeafItems,
    setMessage,
  });

  // 图片检查/清除 hook
  const imageCheckHook = useContentImageCheck({
    effectiveLeafItems,
    projectId,
    draftStore,
    generationMode,
    activeSection,
    setBizLeafItems,
    setLeafItems,
    setMessage,
  });

  // 当 bizOutlineData 变化时同步 bizLeafItems（保留已加载正文，避免覆盖）
  useEffect(() => {
    if (bizOutlineData && Array.isArray(bizOutlineData.outline)) {
      setBizLeafItems(prev => {
        const prevMap = new Map(prev.map(l => [l.id, l.content]));
        return collectLeafItems(bizOutlineData.outline).map(leaf => {
          const existing = prevMap.get(leaf.id);
          return existing ? { ...leaf, content: existing } : leaf;
        });
      });
    }
  }, [bizOutlineData, collectLeafItems]);

  // 获取章节的上级章节信息
  const getParentChapters = useCallback((targetId: string, items: OutlineItem[], parents: OutlineItem[] = []): OutlineItem[] => {
    for (const item of items) {
      if (item.id === targetId) {
        return parents;
      }
      if (item.children && item.children.length > 0) {
        const found = getParentChapters(targetId, item.children, [...parents, item]);
        if (found.length > 0 || item.children.some(child => child.id === targetId)) {
          return found.length > 0 ? found : [...parents, item];
        }
      }
    }
    return [];
  }, []);

  // 获取章节的同级章节信息
  const getSiblingChapters = useCallback((targetId: string, items: OutlineItem[]): OutlineItem[] => {
    // 直接在当前级别查找
    if (items.some(item => item.id === targetId)) {
      return items;
    }
    
    // 递归在子级别查找
    for (const item of items) {
      if (item.children && item.children.length > 0) {
        const siblings = getSiblingChapters(targetId, item.children);
        if (siblings.length > 0) {
          return siblings;
        }
      }
    }
    
    return [];
  }, []);

  useEffect(() => {
    if (outlineData) {
      // 在 full 模式下，如果当前 activeSection 是 'biz'，不更新 tech leafItems
      if (generationMode !== 'full' || activeSection === 'tech') {
        const leaves = collectLeafItems(outlineData.outline);
        // 恢复本地缓存的正文内容（仅对叶子节点生效）
        const filtered = draftStore.filterContentByOutlineLeaves(outlineData.outline);

        // 目录变更时，顺手清理掉无效的旧缓存（只保留当前叶子节点）
        draftStore.saveContentById(filtered);

        // 合并保留已加载/草稿正文：用 setLeafItems(prev=>...) 避免 tab 切换重跑此 effect 时用无正文的 outline 叶子覆盖服务端已加载内容
        setLeafItems(prev => {
          const prevMap = new Map(prev.map(l => [l.id, l.content]));
          return leaves.map(leaf => {
            const cached = filtered[leaf.stable_id || leaf.id];
            const existing = prevMap.get(leaf.id);
            const content = cached || existing || leaf.content || '';
            return { ...leaf, content };
          });
        });
        setProgress(prev => ({ ...prev, total: leaves.length }));
      }
    }
  }, [outlineData, collectLeafItems, generationMode, activeSection]);

  // 监听页面滚动，控制回到顶部按钮的显示
  useEffect(() => {
    // 现在主内容区为内部滚动容器（App.tsx: #app-main-scroll），不能只监听 window
    const scrollContainer = document.getElementById('app-main-scroll');

    const handleScroll = () => {
      const scrollTop = scrollContainer
        ? scrollContainer.scrollTop
        : (window.pageYOffset || document.documentElement.scrollTop);
      setShowScrollToTop(scrollTop > 300);
    };

    // 初始化计算一次，避免刷新后位置不对
    handleScroll();

    const target = scrollContainer || window;
    target.addEventListener('scroll', handleScroll);
    return () => target.removeEventListener('scroll', handleScroll);
  }, []);

  // 组件卸载时清理后台轮询
  useEffect(() => {
    return () => {
      for (const key of Object.keys(bgPollingRef.current)) {
        if (bgPollingRef.current[key]) {
          clearInterval(bgPollingRef.current[key]!);
          bgPollingRef.current[key] = null;
        }
      }
      if (taskListPollingRef.current) {
        clearInterval(taskListPollingRef.current);
        taskListPollingRef.current = null;
      }
    };
  }, []);

  // 页面加载时，检查项目是否有运行中的后台生成任务（支持多设备/多浏览器一致性）
  const lastCheckedProjectRef = useRef<number | null>(null);
  useEffect(() => {
    if (!projectId || !outlineData) return;
    // 自行计算叶子节点（不依赖 leafItems state，避免 React batching 导致空数组）
    const leaves = collectLeafItems(outlineData.outline);
    if (leaves.length === 0) return;
    // 只在该项目首次加载且有叶子节点时检测一次
    if (lastCheckedProjectRef.current === projectId) return;
    lastCheckedProjectRef.current = projectId;

    contentApi.getProjectBatchTask(projectId).then((res) => {
      const data: ProjectBatchTaskResponse = res.data;
      if (data.has_task && data.task_id != null && data.status === 'running') {
        // 该项目有运行中的后台任务，恢复轮询
        const taskId = data.task_id;
        setBgTaskId(taskId);
        setIsGenerating(true);
        setProgress({
          total: data.total ?? 0,
          completed: (data.completed ?? 0) + (data.failed ?? 0),
          current: data.current || '后台生成中...',
          failed: data.failed_titles || [],
          generating: new Set<string>()
        });
        setMessage({
          type: 'success',
          text: `检测到后台任务 #${taskId} 正在运行，当前进度: ${data.completed ?? 0}/${data.total ?? 0}`
        });
        startPolling(taskId, leaves);
      }
    }).catch(() => {
      // 服务不可用，忽略
    });
  }, [projectId, outlineData, collectLeafItems]);

  // 页面加载时，从后端刷新章节版本元数据（不含正文）；正文改为按需按章节懒加载，降低大标书首次加载体积
  // 记录每个 section 是否已基于当前 outline 完成首轮加载；项目切换时重置
  const loadedSectionsRef = useRef<{ projectId: number | null; tech: boolean; biz: boolean }>({
    projectId: null,
    tech: false,
    biz: false,
  });
  const lastReloadTriggerRef = useRef(0);
  const [contentReloadTrigger, setContentReloadTrigger] = useState(0);
  // 章节版本缓存（stable_id -> version），用于乐观锁并发冲突检测
  const chapterMetaRef = useRef<Record<string, number>>({});
  useEffect(() => {
    if (!projectId || !outlineData) return;
    const techLeaves = collectLeafItems(outlineData.outline);
    const bizLeaves = bizOutlineData ? collectLeafItems(bizOutlineData.outline) : [];
    const queue = [
      ...techLeaves.map(l => ({ leaf: l, section: 'tech' as const })),
      ...bizLeaves.map(l => ({ leaf: l, section: 'biz' as const })),
    ];
    if (queue.length === 0) return;

    // 项目切换时重置已加载标记
    if (loadedSectionsRef.current.projectId !== projectId) {
      loadedSectionsRef.current = { projectId, tech: false, biz: false };
      lastReloadTriggerRef.current = 0;
    }

    // 生成完成后 contentReloadTrigger 变化 -> 强制两分部都重新从 DB 加载
    const forceReload = contentReloadTrigger !== lastReloadTriggerRef.current;
    lastReloadTriggerRef.current = contentReloadTrigger;

    // 本次需要加载的分部：
    // - 强制重载时两分部都加载；
    // - 否则仅加载尚未完成首轮加载的分部（修复 bizOutlineData 晚于 outlineData 到达时，
    //   旧守卫误判“商务部分已加载”从而永不加载商务正文的问题）。
    const sectionsToLoad: Array<'tech' | 'biz'> = [];
    if (forceReload || !loadedSectionsRef.current.tech) sectionsToLoad.push('tech');
    if (forceReload || (bizOutlineData && !loadedSectionsRef.current.biz)) sectionsToLoad.push('biz');
    if (sectionsToLoad.length === 0) return;

    const loadQueue = queue.filter(({ section }) => sectionsToLoad.includes(section));

    projectApi.getFullData(projectId).then((res) => {
      const chapterContents = res.data.chapter_contents || [];
      // 记录版本元数据（供乐观锁检测并发编辑冲突）
      const meta: Record<string, number> = {};
      chapterContents.forEach((c: any) => {
        if (c.stable_id != null) meta[c.stable_id] = c.version ?? 0;
      });
      chapterMetaRef.current = meta;

      // 按需懒加载各章节正文（技术/商务两部分；限制并发避免雪崩）
      const MAX = 4;
      const pump = async () => {
        while (loadQueue.length) {
          const { leaf: it, section: sec } = loadQueue.shift()!;
          try {
            const r = await projectApi.getChapterContent(projectId, {
              stable_id: it.stable_id ?? undefined,
              node_id: it.id,
              section: sec,
            });
            if (r.data.exists && r.data.content != null) {
              const setFunc = sec === 'biz' ? setBizLeafItems : setLeafItems;
              setFunc(prev => prev.map(li => (li.id === it.id ? { ...li, content: r.data.content as string } : li)));
              if (it.stable_id && r.data.version != null) chapterMetaRef.current[it.stable_id] = r.data.version;
            }
          } catch {
            // 单章失败不影响整体
          }
        }
      };
      const workers = Array.from({ length: Math.min(MAX, loadQueue.length) }, () => pump());
      Promise.all(workers);
    }).catch(() => {
      // 获取失败不影响主流程
    });

    if (sectionsToLoad.includes('tech')) loadedSectionsRef.current.tech = true;
    if (sectionsToLoad.includes('biz')) loadedSectionsRef.current.biz = true;
  }, [projectId, outlineData, bizOutlineData, collectLeafItems, contentReloadTrigger]);

  // 右侧正文滚动时，通过 IntersectionObserver 高亮左侧目录对应章节
  useEffect(() => {
    if (!outlineData || leafItems.length === 0) return;

    const scrollContainer = contentScrollRef.current;
    if (!scrollContainer) return;

    // 收集所有叶子节点对应的 DOM 元素 ID
    // 也收集有子节点的章节，用于更精确的定位
    const collectChapterIds = (items: OutlineItem[]): string[] => {
      let ids: string[] = [];
      items.forEach(item => {
        ids.push(`chapter-${item.id}`);
        if (item.children && item.children.length > 0) {
          ids = ids.concat(collectChapterIds(item.children));
        }
      });
      return ids;
    };
    const allChapterIds = collectChapterIds(outlineData.outline);

    const observer = new IntersectionObserver(
      (entries) => {
        // 手动滚动期间（点击章节定位），忽略 IntersectionObserver 反馈
        if (isManualScrollingRef.current) return;

        // 找出当前视口中可见的章节
        const visibleEntries = entries.filter(e => e.isIntersecting);
        if (visibleEntries.length === 0) return;

        // 优先取最靠近视口顶部的章节
        visibleEntries.sort((a, b) => {
          const aTop = a.boundingClientRect.top;
          const bTop = b.boundingClientRect.top;
          const aDist = Math.abs(aTop - (a.rootBounds?.top ?? 0));
          const bDist = Math.abs(bTop - (b.rootBounds?.top ?? 0));
          return aDist - bDist;
        });

        if (visibleEntries.length > 0) {
          const chapterId = visibleEntries[0].target.id.replace('chapter-', '');
          setHighlightedItemId(chapterId);
        }
      },
      {
        root: scrollContainer,
        rootMargin: '-10% 0px -70% 0px', // 当章节标题进入视口上方 10% 区域时触发
        threshold: 0,
      }
    );

    // 观察所有章节 DOM 元素
    const observedElements: Element[] = [];
    allChapterIds.forEach(id => {
      const el = document.getElementById(id);
      if (el) {
        observer.observe(el);
        observedElements.push(el);
      }
    });

    return () => {
      observedElements.forEach(el => observer.unobserve(el));
      observer.disconnect();
    };
  }, [outlineData, leafItems]);

  // 当高亮章节变化时，自动滚动左侧目录确保高亮项可见
  // （仅在用户手动滚动正文时触发，点击章节定位时不触发）
  useEffect(() => {
    if (!highlightedItemId || !sidebarScrollRef.current) return;
    // 手动滚动期间跳过，避免点击章节后的二次滚动
    if (isManualScrollingRef.current) return;

    const sidebarEl = sidebarScrollRef.current;
    const highlightedEl = sidebarEl.querySelector(`[data-chapter-id="${highlightedItemId}"]`);
    if (!highlightedEl) return;

    const sidebarRect = sidebarEl.getBoundingClientRect();
    const itemRect = highlightedEl.getBoundingClientRect();

    // 如果高亮项不在可视区域内，滚动使其可见
    if (itemRect.top < sidebarRect.top || itemRect.bottom > sidebarRect.bottom) {
      highlightedEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [highlightedItemId]);

  // 获取叶子节点的实时内容（根据当前 section 选择正确的 state）
  const getLeafItemContent = (itemId: string): string | undefined => {
    const sourceItems = generationMode === 'full' && activeSection === 'biz' ? bizLeafItems : leafItems;
    const leafItem = sourceItems.find(leaf => leaf.id === itemId);
    return leafItem?.content;
  };

  // 检查是否为叶子节点
  const isLeafNode = (item: OutlineItem): boolean => {
    return !item.children || item.children.length === 0;
  };





  // 渲染左侧目录树（带状态和操作按钮）
  const renderOutlineTree = (items: OutlineItem[], level: number = 0): React.ReactElement[] => {
    return items.map(item => {
      const isLeaf = !item.children || item.children.length === 0;
      const status = isLeaf ? getItemStatus(item) : null;
      const isDraggedNode = dragDrop.dragNodeId === item.id;
      const isDropBefore = dragDrop.dropTarget?.id === item.id && dragDrop.dropTarget?.position === 'before';
      const isDropInside = dragDrop.dropTarget?.id === item.id && dragDrop.dropTarget?.position === 'inside';
      const isDropAfter = dragDrop.dropTarget?.id === item.id && dragDrop.dropTarget?.position === 'after';

      const statusConfig = {
        completed: { icon: CheckCircleIcon, cls: 'text-green-500', label: '已完成' },
        generating: { icon: ClockIcon, cls: 'text-blue-500 animate-pulse', label: '生成中' },
        failed: { icon: ExclamationCircleIcon, cls: 'text-red-500', label: '失败' },
        pending: { icon: QueueListIcon, cls: 'text-gray-300', label: '待生成' },
      };

      // 当前高亮章节（用户滚动正文时自动高亮）
      const isHighlighted = highlightedItemId === item.id;

      return (
        <div
          key={item.id}
          data-chapter-id={item.id}
          onDragOver={readOnly ? undefined : (e) => dragDrop.handleNodeDragOver(e, item)}
          onDragLeave={readOnly ? undefined : dragDrop.handleNodeDragLeave}
          onDrop={readOnly ? undefined : (e) => dragDrop.handleNodeDrop(e, item)}
        >
          {/* 拖拽插入位置指示线 - before */}
          {isDropBefore && (
            <div className="h-0.5 bg-blue-500 rounded-full mx-1" style={{ marginLeft: `${level * 16 + 8}px` }} />
          )}
          <div
            draggable={!readOnly}
            onClick={() => isLeaf && scrollToItem(item.id)}
            onDragStart={readOnly ? undefined : (e) => dragDrop.handleNodeDragStart(e, item)}
            onDragEnd={readOnly ? undefined : dragDrop.handleNodeDragEnd}
            className={`group flex items-center py-1 px-2 rounded-md transition-colors ${
              isLeaf ? 'cursor-pointer hover:bg-gray-100' : ''
            } ${editingMetaId === item.id ? 'bg-yellow-50' : ''} ${
              isHighlighted ? 'bg-blue-50 border-l-2 border-blue-400' : ''
            } ${isDraggedNode ? 'opacity-40' : ''} ${
              isDropInside ? 'bg-blue-100 border border-blue-300 border-dashed' : ''
            }`}
            style={{ paddingLeft: `${level * 16 + 8}px` }}
          >
            {/* 状态指示器 */}
            {isLeaf && status && (
              <span className="flex-shrink-0 mr-1.5" title={statusConfig[status].label}>
                {React.createElement(statusConfig[status].icon, {
                  className: `w-3.5 h-3.5 ${statusConfig[status].cls}`
                })}
              </span>
            )}

            {/* 章节标题 - 支持双击行内编辑 */}
            {editingTitleId === item.id ? (
              <div className="flex-1 flex items-center gap-1" onClick={e => e.stopPropagation()}>
                <input
                  ref={titleInputRef}
                  value={editTitleText}
                  onChange={e => setEditTitleText(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') saveEditTitle();
                    if (e.key === 'Escape') cancelEditTitle();
                  }}
                  onBlur={saveEditTitle}
                  className="flex-1 text-xs px-1.5 py-0.5 border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                  autoFocus
                />
                <button onClick={saveEditTitle} className="p-0.5 text-green-500 hover:bg-green-50 rounded">
                  <CheckCircleIcon className="w-2.5 h-2.5" />
                </button>
                <button onClick={cancelEditTitle} className="p-0.5 text-gray-400 hover:bg-gray-100 rounded">
                  <XMarkIcon className="w-2.5 h-2.5" />
                </button>
              </div>
            ) : (
              <span
                className={`text-xs truncate flex-1 cursor-pointer ${
                  editingMetaId === item.id
                    ? 'text-yellow-700 font-medium'
                    : isHighlighted
                      ? 'text-blue-700 font-medium'
                      : isLeaf
                        ? 'text-gray-700'
                        : 'text-gray-500 font-medium'
                }`}
                onDoubleClick={readOnly ? undefined : () => startEditTitle(item)}
                title={readOnly ? '' : "双击编辑标题"}
              >
                {item.id} {item.title}
              </span>
            )}

            {/* 操作按钮（hover 显示）- 所有节点都显示编辑meta按钮，叶子节点额外显示编辑内容和重新生成 */}
            {!isGenerating && !readOnly && (
              <div className="flex-shrink-0 flex items-center space-x-0.5 ml-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {/* 编辑标题/描述/数字范围按钮（所有节点） */}
                {editingMetaId === item.id ? (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); saveEditMeta(item); }}
                      className="p-0.5 rounded text-green-600 hover:bg-green-50"
                      title="保存"
                    >
                      <CheckCircleIcon className="w-3 h-3" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); cancelEditMeta(); }}
                      className="p-0.5 rounded text-gray-400 hover:bg-gray-100"
                      title="取消"
                    >
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </>
                ) : (
                  <button
                    onClick={(e) => { e.stopPropagation(); startEditMeta(item); }}
                    className="p-0.5 rounded text-gray-400 hover:text-purple-500 hover:bg-purple-50"
                    title="编辑标题/描述/数字范围"
                  >
                    <DocumentTextIcon className="w-3 h-3" />
                  </button>
                )}

                {/* 非叶子节点：清空所有子节点内容 */}
                {!isLeaf && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setClearNodeTarget(item); setShowClearNodeContentDialog(true); }}
                    className="p-0.5 rounded text-gray-400 hover:text-red-500 hover:bg-red-50"
                    title="清空所有子节点内容"
                  >
                    <TrashIcon className="w-3 h-3" />
                  </button>
                )}

                {/* 叶子节点：编辑正文内容（打开全屏编辑器） */}
                {isLeaf && (
                  <button
                    onClick={(e) => { e.stopPropagation(); startEditContent(item); }}
                    className="p-0.5 rounded text-gray-400 hover:text-blue-500 hover:bg-blue-50"
                    title="全屏编辑内容"
                  >
                    <PencilSquareIcon className="w-3 h-3" />
                  </button>
                )}

                {/* 叶子节点：重新生成按钮 */}
                {isLeaf && (
                  <button
                    onClick={(e) => handleRegenerateItem(item, e)}
                    disabled={progress.generating.has(item.id)}
                    className="p-0.5 rounded text-gray-400 hover:text-orange-500 hover:bg-orange-50 disabled:opacity-30"
                    title="重新生成"
                  >
                    <ArrowPathIcon className="w-3 h-3" />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* 编辑标题/描述/数字范围 区域 */}
          {editingMetaId === item.id && (
            <div className="mx-2 mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md" style={{ paddingLeft: `${level * 16 + 12}px` }}>
              <div className="mb-2">
                <label className="block text-xs font-medium text-gray-600 mb-0.5">标题</label>
                <input
                  type="text"
                  value={editingMetaTitle}
                  onChange={(e) => setEditingMetaTitle(e.target.value)}
                  className="w-full text-xs border border-gray-300 rounded-md p-1.5 focus:border-purple-500 focus:ring-purple-500"
                  placeholder="输入章节标题..."
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div className="mb-2">
                <div className="flex items-center justify-between mb-0.5">
                  <label className="text-xs font-medium text-gray-600">描述</label>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleAiGenerateDescription(); }}
                    disabled={aiGeneratingDesc}
                    className="flex items-center gap-1 px-2 py-0.5 text-xs text-purple-600 hover:bg-purple-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    title="AI 根据标题自动生成描述"
                  >
                    {aiGeneratingDesc ? (
                      <><ArrowPathIcon className="w-3 h-3 animate-spin" /> 生成中...</>
                    ) : (
                      <><SparklesIcon className="w-3 h-3" /> AI 生成</>
                    )}
                  </button>
                </div>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  className="w-full text-xs border border-gray-300 rounded-md p-1.5 focus:border-purple-500 focus:ring-purple-500 resize-none"
                  rows={3}
                  placeholder="输入章节描述..."
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-0.5">字数范围</label>
                <input
                  type="text"
                  value={editWordCountRange}
                  onChange={(e) => setEditWordCountRange(e.target.value)}
                  className="w-full text-xs border border-gray-300 rounded-md p-1.5 focus:border-purple-500 focus:ring-purple-500"
                  placeholder="如: 800-1500"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          )}

          {/* 子节点 */}
          {item.children && item.children.length > 0 && (
            <>{renderOutlineTree(item.children, level + 1)}</>
          )}
          {/* 拖拽插入位置指示线 - after */}
          {isDropAfter && (
            <div className="h-0.5 bg-blue-500 rounded-full mx-1" style={{ marginLeft: `${level * 16 + 8}px` }} />
          )}
        </div>
      );
    });
  };

  // 渲染目录结构（正文区域）
  const renderOutline = (items: OutlineItem[], level: number = 1): React.ReactElement[] => {
    return items.map((item) => {
      const isLeaf = isLeafNode(item);
      const currentContent = isLeaf ? getLeafItemContent(item.id) : item.content;
      
      return (
        <div key={item.id} id={`chapter-${item.id}`} className={`mb-${level === 1 ? '8' : '4'}`}>
          {/* 标题 */}
          <div className={`text-${level === 1 ? 'xl' : level === 2 ? 'lg' : 'base'} font-${level === 1 ? 'bold' : 'semibold'} text-gray-900 mb-2`}>
            {item.id} {item.title}
          </div>
          
          {/* 描述 */}
          <div className="text-sm text-gray-600 mb-4">
            {item.description}
          </div>

          {/* 内容（仅叶子节点） */}
          {isLeaf && (
            <div className="border-l-4 border-blue-200 pl-4 mb-6">
              {/* 右侧正文区域的编辑按钮 */}
              {currentContent && !readOnly && editingContentId !== item.id && (
                <div className="flex justify-end mb-2 opacity-0 hover:opacity-100 transition-opacity group-hover:opacity-100">
                  <button
                    onClick={() => startDirectEditContent(item.id)}
                    className="inline-flex items-center px-2 py-1 text-xs text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    title="编辑正文内容"
                  >
                    <PencilSquareIcon className="w-3 h-3 mr-1" />
                    编辑
                  </button>
                </div>
              )}

              {/* 直接编辑模式 */}
              {editingContentId === item.id ? (
                <div className="space-y-2">
                  <textarea
                    value={editContentDirect}
                    onChange={(e) => setEditContentDirect(e.target.value)}
                    className="w-full h-64 text-sm border border-blue-300 rounded-md p-3 focus:border-blue-500 focus:ring-blue-500 resize-y font-mono"
                    placeholder="编辑章节内容（支持 Markdown 格式）..."
                  />
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={saveDirectEditContent}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700"
                    >
                      <CheckCircleIcon className="w-3.5 h-3.5 mr-1" />
                      保存
                    </button>
                    <button
                      onClick={cancelDirectEditContent}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded text-gray-600 bg-gray-100 hover:bg-gray-200"
                    >
                      取消
                    </button>
                    <span className="text-xs text-gray-400">支持 Markdown 格式</span>
                  </div>
                </div>
              ) : currentContent ? (
                <div className="group">
                  <MarkdownRenderer content={currentContent} mermaidDefaultView={mermaidDefaultView} />
                </div>
              ) : (
                <div className="text-gray-400 italic py-4">
                  <DocumentTextIcon className="inline w-4 h-4 mr-2" />
                  {progress.generating.has(item.id) ? (
                    <span className="text-blue-600">正在生成内容...</span>
                  ) : (
                    '内容待生成...'
                  )}
                </div>
              )}
            </div>
          )}

          {/* 子章节 */}
          {item.children && item.children.length > 0 && (
            <div className={`ml-${level * 4} mt-4`}>
              {renderOutline(item.children, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  // 生成单个章节内容
  const generateItemContent = async (item: OutlineItem, projectOverview: string, techReq: string, signal?: AbortSignal, section: string = 'tech'): Promise<OutlineItem> => {
    // 正在编辑同一章节且尚未保存：先提示，避免生成结果静默覆盖手工修改
    if (editingContentId === item.id) {
      const proceed = window.confirm(
        `「${item.title}」正在编辑中且尚未保存，继续生成会用模型结果覆盖当前修改。是否继续？`,
      );
      if (!proceed) {
        return item;
      }
    }
    const sectionOutlineData = section === 'biz' ? bizOutlineData : outlineData;
    if (!sectionOutlineData) throw new Error('缺少目录数据');
    
    // 检查是否已被取消
    if (signal?.aborted) throw new DOMException('用户取消了生成操作', 'AbortError');
    
    // 将当前项目添加到正在生成的集合中
    setProgress(prev => {
      const newGenerating = new Set(Array.from(prev.generating));
      newGenerating.add(item.id);
      // 拼接所有正在生成中的章节标题
      const currentLeafItems = section === 'biz' ? bizLeafItems : leafItems;
      const generatingTitles = Array.from(newGenerating).map(id => {
        const leaf = currentLeafItems.find(li => li.id === id);
        return leaf?.title || id;
      });
      return { 
        ...prev, 
        current: generatingTitles.join('; '),
        generating: newGenerating,
      };
    });
    
    try {
      // 获取上级章节和同级章节信息
      const parentChapters = getParentChapters(item.id, sectionOutlineData.outline);
      const siblingChapters = getSiblingChapters(item.id, sectionOutlineData.outline);

      const request: ChapterContentRequest = {
        chapter: item,
        parent_chapters: parentChapters,
        sibling_chapters: siblingChapters,
        project_overview: projectOverview,
        tech_scoring: analysisResults['tech_scoring'] || '',
        tech_requirements: analysisResults['tech_requirements'] || '',
        biz_scoring: analysisResults['biz_scoring'] || '',
        bid_requirements: analysisResults['bid_requirements'] || '',
        commitment: analysisResults['commitment'] || '',
        bid_document_format: analysisResults['bid_document_format'] || '',
        disqualification: analysisResults['disqualification'] || '',
        project_id: projectId,
        section: section,
      };

      const response = await contentApi.generateChapterContentStream(request, signal);

      let content = '';
      const updatedItem = { ...item };
      // 节流控制：每 200ms 最多更新一次 UI，避免高频 setState 导致 Chrome 崩溃
      let lastUpdateTime = 0;
      const THROTTLE_MS = 200;

      await collectSseText(
        response,
        (fullText) => {
          content = fullText;
          updatedItem.content = content;
          draftStore.upsertChapterContent(item, content);
          
          const now = Date.now();
          if (now - lastUpdateTime >= THROTTLE_MS) {
            lastUpdateTime = now;
            const setItemsFunc = section === 'biz' ? setBizLeafItems : setLeafItems;
            setItemsFunc((prevItems) => {
              const newItems = [...prevItems];
              const index = newItems.findIndex((leafItem) => leafItem.id === item.id);
              if (index !== -1) {
                newItems[index] = { ...updatedItem };
              }
              return newItems;
            });
          }
        },
        '章节内容生成失败',
        signal,
      );

      // 流式完成后，确保最终内容写入 UI
      const finalSetFunc = section === 'biz' ? setBizLeafItems : setLeafItems;
      finalSetFunc((prevItems) => {
        const newItems = [...prevItems];
        const index = newItems.findIndex((leafItem) => leafItem.id === item.id);
        if (index !== -1) {
          newItems[index] = { ...updatedItem };
        }
        return newItems;
      });

      // 保存章节内容到数据库（await 确保刷新状态时数据已落库）
      if (projectId && content) {
        try {
          await projectApi.saveChapterContent(projectId, item.id, content, section, item.stable_id);
          // 同步本地版本缓存，避免随后的人工保存因使用旧版本号而误判冲突
          if (item.stable_id) {
            chapterMetaRef.current[item.stable_id] = (chapterMetaRef.current[item.stable_id] ?? 0) + 1;
          }
        } catch {
          // 保存失败不阻塞流程，内容仍在本地草稿中
        }
      }

      return updatedItem;
    } catch (error) {
      // 如果是用户取消，不标记为失败
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error; // 直接向上抛出，不修改 failed 列表
      }
      setProgress(prev => ({
        ...prev,
        failed: [...prev.failed, item.title]
      }));
      throw error;
    } finally {
      // 从正在生成的集合中移除当前项目
      setProgress(prev => {
        const newGenerating = new Set(Array.from(prev.generating));
        newGenerating.delete(item.id);
        const generatingTitles = Array.from(newGenerating).map(id => {
          const sourceItems = section === 'biz' ? bizLeafItems : leafItems;
          const leaf = sourceItems.find(li => li.id === id);
          return leaf?.title || id;
        });
        return {
          ...prev,
          current: generatingTitles.length > 0 ? generatingTitles.join('; ') : '',
          generating: newGenerating
        };
      });
    }
  };

  // 批量生成章节内容（可指定要生成的章节列表）
  const generateBatchItems = async (items: OutlineItem[], clearFailed: boolean = true, section: string = 'tech') => {
    if (!hasFeature(license?.features, 'ai_generate')) {
      setMessage({ type: 'error', text: '未授权「AI 生成」功能，请联系管理员' });
      return;
    }
    // 批量生成会重写这些章节：若仍有章节处于编辑态，先让用户确认
    if (editingContentId) {
      const proceed = window.confirm(
        '有章节正在编辑且尚未保存，批量生成会重写相关章节内容。是否继续？',
      );
      if (!proceed) {
        return;
      }
    }
    const effectiveData = section === 'biz' ? bizOutlineData : outlineData;
    if (!effectiveData || items.length === 0) return;

    // 创建新的 AbortController 用于取消
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsGenerating(true);
    setMessage(null);
    if (clearFailed) {
      setProgress(prev => ({
        total: items.length,
        completed: 0,
        current: '',
        failed: [],
        generating: new Set<string>()
      }));
    } else {
      // 重新生成失败章节时，从 failed 列表中移除即将重试的章节标题
      const retryTitles = new Set(items.map(i => i.title));
      setProgress(prev => ({
        ...prev,
        current: '',
        failed: prev.failed.filter(t => !retryTitles.has(t)),
        generating: new Set<string>()
      }));
    }

    try {
      const effectiveConcurrency = Math.max(1, Math.min(10, concurrency));
      const currentLeafItems = section === 'biz' ? [...bizLeafItems] : [...leafItems];
      
      for (let i = 0; i < items.length; i += effectiveConcurrency) {
        // 每批开始前检查是否已取消
        if (abortController.signal.aborted) break;
        
        const batch = items.slice(i, i + effectiveConcurrency);
        const promises = batch.map(item => 
          generateItemContent(item, effectiveData.project_overview || '', techRequirements, abortController.signal, section)
            .then(updatedItem => {
              const index = currentLeafItems.findIndex(ui => ui.id === updatedItem.id);
              if (index !== -1) {
                currentLeafItems[index] = updatedItem;
              }
              setProgress(prev => ({ ...prev, completed: prev.completed + 1 }));
              return updatedItem;
            })
            .catch((error) => {
              // AbortError 不增加 completed 计数
              if (error instanceof DOMException && error.name === 'AbortError') {
                return item;
              }
              setProgress(prev => ({ ...prev, completed: prev.completed + 1 }));
              return item; // 返回原始项目
            })
        );

        await Promise.all(promises);
      }

      // 如果被取消了，只更新已生成的部分
      const setItemsFunc = section === 'biz' ? setBizLeafItems : setLeafItems;
      setItemsFunc(currentLeafItems);

      // 前台生成完成后同步项目状态（草稿 → 编辑中 / 已完成）
      if (projectId && !abortController.signal.aborted) {
        try {
          await contentApi.refreshProjectStatus(projectId);
        } catch {
          // 静默失败，onProjectRefresh 会兜底重新拉取项目数据
        }
        if (onProjectRefresh) {
          onProjectRefresh();
        }
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        setMessage({ type: 'success', text: '生成已终止' });
      } else {
        setMessage({ type: 'error', text: getErrorMessage(error, '生成内容失败') });
      }
    } finally {
      setIsGenerating(false);
      setProgress(prev => ({ ...prev, current: '', generating: new Set<string>() }));
      // 清理 AbortController 引用
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  };

  // 终止生成
  const handleCancelGeneration = async () => {
    // 1. 前台模式：通过 AbortController 取消所有正在进行的 fetch 请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // 2. 后台模式：调用后端取消接口
    if (bgTaskId) {
      try {
        await contentApi.cancelBatch(bgTaskId);
      } catch (err) {
        console.warn('取消后台任务失败:', err);
      }
    }

    // 3. 清理轮询（清除所有 section 的轮询）
    for (const key of Object.keys(bgPollingRef.current)) {
      if (bgPollingRef.current[key]) {
        clearInterval(bgPollingRef.current[key]!);
        bgPollingRef.current[key] = null;
      }
    }

    setIsGenerating(false);
    setBgTaskId(null);
    setProgress(prev => ({ ...prev, current: '', generating: new Set<string>() }));
    setMessage({ type: 'success', text: '生成已终止' });
  };

  // 开始生成所有内容（只生成未完成的章节）
  const handleGenerateContent = async () => {
    if (!effectiveOutlineData || effectiveLeafItems.length === 0) return;
    const pendingItems = effectiveLeafItems.filter(item => !item.content || item.content.trim().length === 0);
    if (pendingItems.length === 0) {
      setMessage({ type: 'success', text: '所有章节已生成完毕' });
      return;
    }

    // 点击"生成标书"立即将项目状态更新为"编辑中"（无需等待，即时反馈）
    if (onProjectEditing) {
      onProjectEditing();
    }

    const section = generationMode === 'full' ? activeSection : 'tech';

    if (generateMode === 'foreground') {
      // 前台模式：现有流式逻辑
      await generateBatchItems(pendingItems, true, section);
    } else {
      // 后台模式：提交任务 + 轮询进度
      await startBackgroundGeneration(pendingItems, section);
    }
  };

  // 后台模式：提交批量生成任务
  const startBackgroundGeneration = async (items: OutlineItem[], section: string = 'tech') => {
    if (!projectId) {
      setMessage({ type: 'error', text: '请先保存项目' });
      return;
    }

    setIsGenerating(true);
    setMessage(null);
    setProgress({
      total: items.length,
      completed: 0,
      current: '提交后台任务...',
      failed: [],
      generating: new Set<string>()
    });

    try {
      const requestData: BatchGenerateRequest = {
        project_id: projectId,
        chapters: items.map(item => ({
          id: item.id,
          stable_id: item.stable_id || null,
          title: item.title,
          description: item.description,
          word_count_range: (item as any).word_count_range,
          source_requirement_id: (item as any).source_requirement_id,
          source_requirement_title: (item as any).source_requirement_title,
        })),
        project_overview: effectiveOutlineData?.project_overview || '',
        tech_scoring: analysisResults['tech_scoring'] || '',
        tech_requirements: analysisResults['tech_requirements'] || '',
        biz_scoring: analysisResults['biz_scoring'] || '',
        bid_requirements: analysisResults['bid_requirements'] || '',
        commitment: analysisResults['commitment'] || '',
        bid_document_format: analysisResults['bid_document_format'] || '',
        disqualification: analysisResults['disqualification'] || '',
        concurrency: Math.max(1, Math.min(10, concurrency)),
        section: section,
      };

      const res = await contentApi.generateAllBatch(requestData);
      const taskId = res.data.task_id;
      setBgTaskId(taskId);
      setMessage({ type: 'success', text: `后台任务已提交 (ID: ${taskId})，关闭页面不影响生成` });

      // 开始轮询进度
      startPolling(taskId, items, section);
    } catch (err) {
      setMessage({ type: 'error', text: getErrorMessage(err, '提交后台任务失败') });
      setIsGenerating(false);
    }
  };

  // 轮询后台任务进度
  const startPolling = (taskId: number, items: OutlineItem[], section: string = 'tech') => {
    // 清除该 section 的旧轮询
    if (bgPollingRef.current[section]) {
      clearInterval(bgPollingRef.current[section]!);
    }

    const setItemsFunc = section === 'biz' ? setBizLeafItems : setLeafItems;

    const poll = async () => {
      try {
        const res = await contentApi.getGenerateStatus(taskId);
        const status: GenerateStatusResponse = res.data;

        // 计算正在生成的章节：已提交但不在 results 中、也不在 failed_titles 中
        const completedIds = new Set(Object.keys(status.results));
        const failedTitleSet = new Set(status.failed_titles || []);
        const generatingIds = new Set<string>();
        items.forEach(item => {
          if (!completedIds.has(item.id) && !failedTitleSet.has(item.title)) {
            generatingIds.add(item.id);
          }
        });

        setProgress(prev => ({
          ...prev,
          total: status.total,
          completed: status.completed + status.failed,
          current: status.current || (status.status === 'completed' ? '生成完成' : '后台生成中...'),
          failed: status.failed_titles || [],
          generating: status.status === 'running' ? generatingIds : new Set<string>(),
        }));

        // 更新叶子节点内容（从 results 中读取）
        if (Object.keys(status.results).length > 0) {
          setItemsFunc(prevItems => {
            const newItems = [...prevItems];
            let changed = false;
            for (const [nodeId, content] of Object.entries(status.results)) {
              const idx = newItems.findIndex(li => li.id === nodeId);
              if (idx !== -1 && content !== newItems[idx].content) {
                newItems[idx] = { ...newItems[idx], content };
                draftStore.upsertChapterContent(newItems[idx], content);
                changed = true;
                changed = true;
              }
            }
            return changed ? newItems : prevItems;
          });
        }

        if (status.status === 'completed' || status.status === 'failed' || status.status === 'cancelled') {
          // 任务结束，停止该 section 的轮询
          if (bgPollingRef.current[section]) {
            clearInterval(bgPollingRef.current[section]!);
            bgPollingRef.current[section] = null;
          }
          setIsGenerating(false);
          setBgTaskId(null);
          setProgress(prev => ({ ...prev, current: '', generating: new Set<string>() }));
          if (status.status === 'cancelled') {
            setMessage({ type: 'success', text: '后台生成已终止' });
          } else {
            setMessage({
              type: status.failed > 0 ? 'error' : 'success',
              text: status.failed > 0
                ? `后台生成完成：${status.completed} 成功, ${status.failed} 失败`
                : `后台生成完成：${status.completed} 个章节全部生成完毕`
            });
          }
          // 兜底：主动触发后端状态同步（避免后台任务中函数静默失败导致状态一直是 draft）
          if (projectId && status.status !== 'cancelled') {
            try {
              await contentApi.refreshProjectStatus(projectId);
            } catch {
              // 状态同步失败不阻塞 UI
            }
          }
          // 通知父组件刷新项目状态（含侧边栏项目列表）
          if (onProjectRefresh) {
            onProjectRefresh();
          }
          // 强制重新从 DB 加载所有章节内容（确保 UI 与后端完全同步）
          setContentReloadTrigger(prev => prev + 1);
        }
      } catch (err) {
        // 轮询出错不中断
        console.warn('轮询进度失败:', err);
      }
    };

    bgPollingRef.current[section] = setInterval(poll, 3000);
    // 立即执行一次
    poll();
  };

  // 重新生成失败的章节
  const handleRetryFailed = async () => {
    if (!effectiveOutlineData || progress.failed.length === 0) return;
    // 根据失败标题找回对应的 OutlineItem
    const failedTitles = new Set(progress.failed);
    const failedItems = effectiveLeafItems.filter(item => failedTitles.has(item.title));
    if (failedItems.length === 0) {
      setMessage({ type: 'error', text: '未找到失败的章节，请尝试重新生成全部内容' });
      return;
    }
    const section = generationMode === 'full' ? activeSection : 'tech';
    await generateBatchItems(failedItems, false, section);
  };

  // 重新生成单个章节
  const handleRegenerateItem = async (item: OutlineItem, e: React.MouseEvent) => {
    if (!hasFeature(license?.features, 'ai_generate')) {
      setMessage({ type: 'error', text: '未授权「AI 生成」功能，请联系管理员' });
      return;
    }
    e.stopPropagation();
    if (!effectiveOutlineData || isGenerating) return;
    const section = generationMode === 'full' ? activeSection : 'tech';
    await generateBatchItems([item], false, section);
  };

  // 获取叶子节点的生成状态
  const getItemStatus = (item: OutlineItem): 'completed' | 'generating' | 'failed' | 'pending' => {
    if (progress.generating.has(item.id)) return 'generating';
    if (progress.failed.includes(item.title)) return 'failed';
    const currentLeafItems = generationMode === 'full' && activeSection === 'biz' ? bizLeafItems : leafItems;
    const leafItem = currentLeafItems.find(l => l.id === item.id);
    if (leafItem?.content) return 'completed';
    return 'pending';
  };

  // 打开全屏编辑器
  const startEditContent = (item: OutlineItem) => {
    const currentLeafItems = generationMode === 'full' && activeSection === 'biz' ? bizLeafItems : leafItems;
    const leafItem = currentLeafItems.find(l => l.id === item.id);
    setFullscreenEditorItem({ ...item, content: leafItem?.content || item.content || '' });
  };

  // 全屏编辑器保存回调
  const handleFullscreenSave = (content: string) => {
    if (!fullscreenEditorItem) return;
    const setItemsFunc = generationMode === 'full' && activeSection === 'biz' ? setBizLeafItems : setLeafItems;
    setItemsFunc(prev => prev.map(item =>
      item.id === fullscreenEditorItem.id ? { ...item, content } : item
    ));
    draftStore.upsertChapterContent(fullscreenEditorItem, content);
    if (projectId) {
      const section = generationMode === 'full' ? activeSection : 'tech';
      const ver = fullscreenEditorItem.stable_id ? chapterMetaRef.current[fullscreenEditorItem.stable_id] : undefined;
      projectApi.saveChapterContent(projectId, fullscreenEditorItem.id, content, section, fullscreenEditorItem.stable_id, ver)
        .then(() => {
          if (fullscreenEditorItem.stable_id) {
            chapterMetaRef.current[fullscreenEditorItem.stable_id] = (chapterMetaRef.current[fullscreenEditorItem.stable_id] ?? 0) + 1;
          }
        })
        .catch((e: any) => {
          if (e?.response?.status === 409) {
            setMessage({ type: 'error', text: e?.response?.data?.message || '该章节已被他人修改，请刷新后重试。' });
          }
        });
    }
  };

  // 全屏编辑器：上一章/下一章导航（优先用已懒加载缓存，缺失则按需拉取）
  const fsChapters = generationMode === 'full' && activeSection === 'biz' ? bizLeafItems : leafItems;
  const fsIdx = fullscreenEditorItem ? fsChapters.findIndex(c => c.id === fullscreenEditorItem.id) : -1;
  const fsCanPrev = fsIdx > 0;
  const fsCanNext = fsIdx >= 0 && fsIdx < fsChapters.length - 1;
  const fsChapterLabel = fullscreenEditorItem && fsIdx >= 0
    ? `第 ${fsIdx + 1} / ${fsChapters.length} 章`
    : '';

  const openFullscreenChapter = (target: OutlineItem) => {
    const list = generationMode === 'full' && activeSection === 'biz' ? bizLeafItems : leafItems;
    const local = list.find(l => l.id === target.id);
    if (local?.content) {
      setFullscreenEditorItem({ ...target, content: local.content });
      return;
    }
    // 本地尚未加载到正文：按需拉取
    if (!projectId) {
      setFullscreenEditorItem({ ...target, content: '' });
      return;
    }
    const section = generationMode === 'full' && activeSection === 'biz' ? 'biz' : 'tech';
    const setFunc = section === 'biz' ? setBizLeafItems : setLeafItems;
    projectApi.getChapterContent(projectId, {
      stable_id: target.stable_id ?? undefined,
      node_id: target.id,
      section,
    }).then((r) => {
      if (r.data.exists && r.data.content != null) {
        const content = r.data.content as string;
        setFunc(prev => prev.map(l => l.id === target.id ? { ...l, content } : l));
        setFullscreenEditorItem({ ...target, content });
        if (target.stable_id && r.data.version != null) {
          chapterMetaRef.current[target.stable_id] = r.data.version;
        }
      } else {
        setFullscreenEditorItem({ ...target, content: '' });
      }
    }).catch(() => {
      setFullscreenEditorItem({ ...target, content: '' });
    });
  };

  const goPrevChapter = () => {
    if (fsIdx <= 0) return;
    const target = fsChapters[fsIdx - 1];
    if (target) openFullscreenChapter(target);
  };

  const goNextChapter = () => {
    if (fsIdx < 0 || fsIdx >= fsChapters.length - 1) return;
    const target = fsChapters[fsIdx + 1];
    if (target) openFullscreenChapter(target);
  };

  // 开始直接编辑右侧正文内容
  const startDirectEditContent = (itemId: string) => {
    const currentLeafItems = generationMode === 'full' && activeSection === 'biz' ? bizLeafItems : leafItems;
    const leafItem = currentLeafItems.find(l => l.id === itemId);
    if (!leafItem?.content) return;
    setEditingContentId(itemId);
    setEditContentDirect(leafItem.content);
  };

  // 保存右侧直接编辑的正文内容
  const saveDirectEditContent = () => {
    if (!editingContentId) return;
    const currentItems = generationMode === 'full' && activeSection === 'biz' ? bizLeafItems : leafItems;
    const setItemsFunc = generationMode === 'full' && activeSection === 'biz' ? setBizLeafItems : setLeafItems;
    setItemsFunc(prev => prev.map(item =>
      item.id === editingContentId ? { ...item, content: editContentDirect } : item
    ));
    const foundItem = currentItems.find(item => item.id === editingContentId);
    draftStore.upsertChapterContent(foundItem || editingContentId, editContentDirect);
    if (projectId) {
      const section = generationMode === 'full' ? activeSection : 'tech';
      const ver = foundItem?.stable_id ? chapterMetaRef.current[foundItem.stable_id] : undefined;
      projectApi.saveChapterContent(projectId, editingContentId, editContentDirect, section, foundItem?.stable_id, ver)
        .then(() => {
          if (foundItem?.stable_id) {
            chapterMetaRef.current[foundItem.stable_id] = (chapterMetaRef.current[foundItem.stable_id] ?? 0) + 1;
          }
        })
        .catch((e: any) => {
          if (e?.response?.status === 409) {
            setMessage({ type: 'error', text: e?.response?.data?.message || '该章节已被他人修改，请刷新后重试。' });
          }
        });
    }
    setEditingContentId(null);
    setEditContentDirect('');
  };

  // 取消右侧直接编辑
  const cancelDirectEditContent = () => {
    setEditingContentId(null);
    setEditContentDirect('');
  };

  // 开始编辑描述/数字范围
  const startEditMeta = (item: OutlineItem) => {
    setEditingMetaId(item.id);
    setEditingMetaTitle(item.title || '');
    setEditDescription(item.description || '');
    setEditWordCountRange(item.word_count_range || '');
  };

  // 取消编辑描述/数字范围
  const cancelEditMeta = () => {
    setEditingMetaId(null);
    setEditingMetaTitle('');
    setEditDescription('');
    setEditWordCountRange('');
    setAiGeneratingDesc(false);
  };

  // 开始行内编辑标题
  const startEditTitle = (item: OutlineItem) => {
    setEditingTitleId(item.id);
    setEditTitleText(item.title || '');
    setTimeout(() => titleInputRef.current?.focus(), 50);
  };

  // 保存行内编辑标题
  const saveEditTitle = () => {
    if (!editingTitleId) return;
    const trimmed = editTitleText.trim();
    if (!trimmed) return;
    setLeafItems(prev =>
      prev.map(o => updateOutlineItemTitle(o, editingTitleId, trimmed))
    );
    // 同步更新 outlineData，使侧边栏立即反映标题变更
    const currentData = activeSection === 'biz' ? bizOutlineData : outlineData;
    if (currentData) {
      const updatedOutline = currentData.outline.map(o => updateOutlineItemTitle(o, editingTitleId, trimmed));
      if (activeSection === 'biz' && onBizOutlineUpdated) {
        onBizOutlineUpdated({ ...currentData, outline: updatedOutline });
      } else if (onOutlineUpdated) {
        onOutlineUpdated({ ...currentData, outline: updatedOutline });
      }
    }
    // 后台保存到数据库
    if (projectId) {
      const flattenOutlineItems = (items: OutlineItem[], parentId: string | null = null): Array<{
        id: string; parent_node_id: string | null; title: string;
        description: string; word_count_range?: string | null;
        source_requirement_id?: string | null; source_requirement_title?: string | null;
        section: string;
      }> => {
        const result: ReturnType<typeof flattenOutlineItems> = [];
        for (const o of items) {
          result.push({
            id: o.id, parent_node_id: parentId,
            title: o.id === editingTitleId ? trimmed : o.title,
            description: o.description || '', word_count_range: o.word_count_range || null,
            source_requirement_id: o.source_requirement_id || null,
            source_requirement_title: o.source_requirement_title || null,
            section,
          });
          if (o.children && o.children.length > 0) {
            result.push(...flattenOutlineItems(o.children, o.id));
          }
        }
        return result;
      };
      const section = activeSection === 'biz' && generationMode === 'full' ? 'biz' : 'tech';
      projectApi.saveOutline(projectId, flattenOutlineItems(currentData?.outline || [])).catch(() => {});
    }
    setEditingTitleId(null);
  };

  // 取消行内编辑标题
  const cancelEditTitle = () => {
    setEditingTitleId(null);
  };

  // 递归更新 outlineItem 的 title
  const updateOutlineItemTitle = (item: OutlineItem, targetId: string, newTitle: string): OutlineItem => {
    if (item.id === targetId) {
      return { ...item, title: newTitle };
    }
    if (item.children && item.children.length > 0) {
      return { ...item, children: item.children.map(c => updateOutlineItemTitle(c, targetId, newTitle)) };
    }
    return item;
  };

  // AI 生成描述
  const handleAiGenerateDescription = async () => {
    if (!editingMetaTitle || !projectId || aiGeneratingDesc) return;
    setAiGeneratingDesc(true);
    try {
      const response = await projectApi.generateNodeDescription(projectId, editingMetaTitle);
      if (response.data.description) {
        setEditDescription(response.data.description);
      }
    } catch (err) {
      setMessage({ type: 'error', text: getErrorMessage(err, 'AI 生成描述失败') });
    } finally {
      setAiGeneratingDesc(false);
    }
  };

  // 保存编辑的标题/描述/数字范围
  const saveEditMeta = (_item: OutlineItem) => {
    if (!editingMetaId || !effectiveOutlineData) return;

    // 递归更新 outline 树中对应节点的 title、description 和 word_count_range
    const updateOutlineItem = (items: OutlineItem[]): OutlineItem[] => {
      return items.map(o => {
        if (o.id === editingMetaId) {
          return { ...o, title: editingMetaTitle.trim() || o.title, description: editDescription, word_count_range: editWordCountRange };
        }
        if (o.children && o.children.length > 0) {
          return { ...o, children: updateOutlineItem(o.children) };
        }
        return o;
      });
    };

    const currentData = effectiveOutlineData;
    const updatedOutline = updateOutlineItem(currentData.outline);

    // 保存到后端
    if (projectId) {
      const section = generationMode === 'full' ? activeSection : 'tech';
      // 需要 flattenOutlineItems 工具函数，这里内联实现
      const flattenOutlineItems = (items: OutlineItem[], parentId: string | null = null): Array<{
        id: string;
        parent_node_id: string | null;
        title: string;
        description: string;
        word_count_range?: string | null;
        source_requirement_id?: string | null;
        source_requirement_title?: string | null;
        section: string;
      }> => {
        const result: ReturnType<typeof flattenOutlineItems> = [];
        for (const o of items) {
          result.push({
            id: o.id,
            parent_node_id: parentId,
            title: o.title,
            description: o.description || '',
            word_count_range: o.word_count_range || null,
            source_requirement_id: o.source_requirement_id || null,
            source_requirement_title: o.source_requirement_title || null,
            section,
          });
          if (o.children && o.children.length > 0) {
            result.push(...flattenOutlineItems(o.children, o.id));
          }
        }
        return result;
      };

      projectApi.saveOutline(projectId, flattenOutlineItems(updatedOutline)).catch(() => {});
    }

    // 通过回调更新 AppState 中的 outlineData
    const updatedOutlineData: OutlineData = {
      ...currentData,
      outline: updatedOutline,
    };
    if (activeSection === 'biz' && generationMode === 'full') {
      onBizOutlineUpdated?.(updatedOutlineData);
    } else if (onOutlineUpdated) {
      onOutlineUpdated(updatedOutlineData);
    }

    setEditingMetaId(null);
    setEditDescription('');
    setEditWordCountRange('');
  };

  // 侧边栏拖拽调整宽度
  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizingRef.current = true;
    resizeStartXRef.current = e.clientX;
    resizeStartWidthRef.current = sidebarWidth;
    setIsResizingSidebar(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [sidebarWidth]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizingRef.current) return;
      const delta = e.clientX - resizeStartXRef.current;
      const newWidth = Math.max(200, Math.min(600, resizeStartWidthRef.current + delta));
      setSidebarWidth(newWidth);
    };
    const handleMouseUp = () => {
      if (isResizingRef.current) {
        isResizingRef.current = false;
        setIsResizingSidebar(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // 滚动到指定章节（点击左侧目录时触发）
  const scrollToItem = (itemId: string) => {
    // 标记为手动滚动，阻止 IntersectionObserver 反馈导致侧边栏二次滚动
    isManualScrollingRef.current = true;
    if (manualScrollTimerRef.current) clearTimeout(manualScrollTimerRef.current);
    // 1 秒后恢复自动高亮，确保平滑滚动结束后 IntersectionObserver 恢复正常
    manualScrollTimerRef.current = setTimeout(() => {
      isManualScrollingRef.current = false;
    }, 1000);

    const el = document.getElementById(`chapter-${itemId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // 获取叶子节点的最新内容（包括生成的内容）
  const getLatestContent = useCallback((item: OutlineItem): string => {
    if (!item.children || item.children.length === 0) {
      // 叶子节点，从有效的 leafItems 获取最新内容
      const currentLeafItems = generationMode === 'full' && activeSection === 'biz' ? bizLeafItems : leafItems;
      const leafItem = currentLeafItems.find(leaf => leaf.id === item.id);
      return leafItem?.content || item.content || '';
    }
    return item.content || '';
  }, [leafItems, bizLeafItems, generationMode, activeSection]);

  // 解析Markdown内容为Word段落
  // （已提取到文件顶层，供后续导出Word等复用）

  // 滚动到页面顶部
  const scrollToTop = () => {
    const scrollContainer = document.getElementById('app-main-scroll');
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 版本恢复回调：从版本快照重建 leafItems 和 outlineData（按 section 分离）
  const handleVersionRestore = useCallback(
    (outlineNodes: any[], chapterContents: Record<string, string>) => {
      if (!outlineData) return;

      // 重建 OutlineItem 树
      const buildTree = (nodes: any[]): OutlineItem[] => {
        const nodeMap: Record<string, OutlineItem> = {};
        nodes.forEach((n: any) => {
          nodeMap[n.node_id] = {
            id: n.node_id,
            title: n.title,
            description: n.description || '',
            word_count_range: n.word_count_range || '800-1500',
            source_requirement_id: n.source_requirement_id || undefined,
            source_requirement_title: n.source_requirement_title || undefined,
            content: chapterContents[n.node_id] || undefined,
          };
        });
        const roots: OutlineItem[] = [];
        nodes.forEach((n: any) => {
          const node = nodeMap[n.node_id];
          if (!n.parent_node_id || !nodeMap[n.parent_node_id]) {
            roots.push(node);
          } else {
            const parent = nodeMap[n.parent_node_id];
            if (!parent.children) parent.children = [];
            parent.children.push(node);
          }
        });
        return roots;
      };

      const collectLeaves = (items: OutlineItem[]): OutlineItem[] => {
        let leaves: OutlineItem[] = [];
        items.forEach((item) => {
          if (!item.children || item.children.length === 0) {
            leaves.push(item);
          } else {
            leaves = leaves.concat(collectLeaves(item.children));
          }
        });
        return leaves;
      };

      // 按 section 分离节点
      const techNodes = outlineNodes.filter((n: any) => (n.section || 'tech') === 'tech');
      const bizNodes = outlineNodes.filter((n: any) => n.section === 'biz');

      // 更新 localStorage 缓存（保留所有内容）
      const contentMap: Record<string, string> = {};
      Object.entries(chapterContents).forEach(([nid, content]) => {
        if (content) {
          contentMap[nid] = content;
        }
      });
      draftStore.saveContentById(contentMap);

      // 重建技术部分
      if (techNodes.length > 0) {
        const restoredTechOutline = buildTree(techNodes);
        if (!onOutlineUpdated) return;
        onOutlineUpdated({
          ...outlineData,
          outline: restoredTechOutline,
        });
      }

      // 重建商务部分（仅整套投标文件模式）
      if (bizNodes.length > 0 && onBizOutlineUpdated && bizOutlineData) {
        const restoredBizOutline = buildTree(bizNodes);
        onBizOutlineUpdated({
          ...bizOutlineData,
          outline: restoredBizOutline,
        });
        // 如果当前 activeSection 是 biz，更新 biz leaf items
        const setItemsFunc = generationMode === 'full' && activeSection === 'biz' ? setBizLeafItems : setLeafItems;
        setItemsFunc(collectLeaves(generationMode === 'full' && activeSection === 'biz' ? restoredBizOutline : buildTree(techNodes)));
      } else {
        // 非整套模式或没有 biz 节点：更新当前 section 的 leaf items
        const restoredOutline = buildTree(techNodes.length > 0 ? techNodes : outlineNodes);
        const setItemsFunc = generationMode === 'full' && activeSection === 'biz' ? setBizLeafItems : setLeafItems;
        setItemsFunc(collectLeaves(restoredOutline));
      }
    },
    [outlineData, bizOutlineData, onOutlineUpdated, onBizOutlineUpdated, draftStore, generationMode, activeSection, setBizLeafItems, setLeafItems]
  );

  // 计算当前 tokens 和章节数（用于版本保存）
  const currentTokens = effectiveLeafItems.reduce((sum, item) => {
    // 粗略估算 tokens：中文约 1.5 字符/token，英文约 4 字符/token
    const text = item.content || '';
    return sum + Math.ceil(text.length / 2);
  }, 0);
  const currentGeneratedChapters = effectiveLeafItems.filter((item) => item.content).length;

  // 构建当前章节内容 Map（用于版本对比时作为"当前版本"）
  const currentChapterContentsForDiff = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    effectiveLeafItems.forEach((item) => {
      if (item.content) {
        map[item.id] = item.content;
      }
    });
    return map;
  }, [effectiveLeafItems]);

  // 构建当前目录节点列表（用于版本对比时作为"当前版本"）
  const currentOutlineNodesForDiff = useMemo(() => {
    if (!effectiveOutlineData) return [];
    const flatten = (items: OutlineItem[]): Array<{
      node_id: string;
      parent_node_id: string | null;
      title: string;
      description: string;
      word_count_range?: string | null;
      source_requirement_id?: string | null;
      source_requirement_title?: string | null;
    }> => {
      let nodes: Array<{
        node_id: string;
        parent_node_id: string | null;
        title: string;
        description: string;
        word_count_range?: string | null;
        source_requirement_id?: string | null;
        source_requirement_title?: string | null;
      }> = [];
      const traverse = (itemList: OutlineItem[], parentId: string | null) => {
        itemList.forEach((item) => {
          nodes.push({
            node_id: item.id,
            parent_node_id: parentId,
            title: item.title,
            description: item.description || '',
            word_count_range: item.word_count_range || undefined,
            source_requirement_id: item.source_requirement_id || undefined,
            source_requirement_title: item.source_requirement_title || undefined,
          });
          if (item.children && item.children.length > 0) {
            traverse(item.children, item.id);
          }
        });
      };
      traverse(items, null);
      return nodes;
    };
    return flatten(effectiveOutlineData.outline);
  }, [effectiveOutlineData]);

  // DiffViewer 中编辑"当前版本"时的保存回调
  const handleDiffUpdateCurrent = useCallback(async (nodeId: string, content: string) => {
    // 查找 nodeId 在 tech 或 biz leafItems 中
    const updateItems = (setFunc: typeof setLeafItems) => {
      setFunc((prevItems) => {
        const newItems = [...prevItems];
        const index = newItems.findIndex((item) => item.id === nodeId);
        if (index !== -1) {
          newItems[index] = { ...newItems[index], content };
        }
        return newItems;
      });
    };

    // 检查 tech items
    const isTech = leafItems.some((item) => item.id === nodeId);
    // 检查 biz items
    const isBiz = bizLeafItems.some((item) => item.id === nodeId);

    if (isTech) updateItems(setLeafItems);
    if (isBiz) updateItems(setBizLeafItems);

    // 如果都不在，可能只存在于 effectiveLeafItems（通过 effectiveOutlineData 推断）
    if (!isTech && !isBiz) {
      // 根据当前 activeSection 写入
      if (generationMode === 'full' && activeSection === 'biz') {
        updateItems(setBizLeafItems);
      } else {
        updateItems(setLeafItems);
      }
    }

    // 保存到 draftStore
    const fullItem = isTech
      ? leafItems.find((item) => item.id === nodeId)
      : isBiz
        ? bizLeafItems.find((item) => item.id === nodeId)
        : effectiveLeafItems.find((item) => item.id === nodeId);

    if (fullItem) {
      draftStore.upsertChapterContent(fullItem, content);
    }
  }, [leafItems, bizLeafItems, effectiveLeafItems, generationMode, activeSection, draftStore, setLeafItems, setBizLeafItems]);

  // 构建导出数据（复用逻辑，根据 activeSection 确定导出的目录结构）
  const buildExportPayload = useCallback(() => {
    // 确定当前有效的目录数据和叶子节点
    const isBizActive = generationMode === 'full' && activeSection === 'biz';
    const exportOutlineData = isBizActive ? bizOutlineData : outlineData;
    const exportLeafItems = isBizActive ? bizLeafItems : leafItems;
    
    if (!exportOutlineData) return null;

    const buildExportOutline = (items: OutlineItem[]): OutlineItem[] => {
      return items.map(item => {
        const leafItem = exportLeafItems.find(l => l.id === item.id);
        const isLeaf = !item.children || item.children.length === 0;
        const exportedItem: OutlineItem = {
          ...item,
          content: isLeaf ? (leafItem?.content ?? item.content ?? '') : (item.content || ''),
        };
        if (item.children && item.children.length > 0) {
          exportedItem.children = buildExportOutline(item.children);
        }
        return exportedItem;
      });
    };
    
    const sectionLabel = isBizActive ? '商务部分' : (generationMode === 'full' ? '技术部分' : '');
    const projectTitle = exportOutlineData.project_name || projectName;

    const payload: any = {
      project_name: projectTitle,
      outline: buildExportOutline(exportOutlineData.outline),
      generation_mode: generationMode,
    };
    // full 模式：将 section 标题作为独立字段传递，后端会插入为一级标题
    if (generationMode === 'full' && sectionLabel) {
      payload.section_label = sectionLabel;
    }
    // 传递企业名称，用于封面页
    if (companyName) {
      payload.company_name = companyName;
    }
    // 传递投标文件格式要求，用于封面格式参考
    if (analysisResults['bid_document_format']) {
      payload.bid_document_format = analysisResults['bid_document_format'];
    }
    return payload;
  }, [outlineData, bizOutlineData, projectName, companyName, generationMode, activeSection, leafItems, bizLeafItems, analysisResults]);

  // 导出Word文档 - 弹出模板选择对话框
  const handleExportWord = () => {
    const exportPayload = buildExportPayload();
    if (!exportPayload) return;
    fullExportModeRef.current = false;
    setShowExportDialog(true);
  };

  // 构建完整 Word 导出（合并商务+技术）的 payload
  const buildFullExportPayload = useCallback(() => {
    if (!outlineData || !bizOutlineData) return null;

    const buildExportOutline = (items: OutlineItem[], leafItemsList: OutlineItem[]): OutlineItem[] => {
      return items.map(item => {
        const leafItem = leafItemsList.find(l => l.id === item.id);
        const isLeaf = !item.children || item.children.length === 0;
        const exportedItem: OutlineItem = {
          ...item,
          content: isLeaf ? (leafItem?.content ?? item.content ?? '') : (item.content || ''),
        };
        if (item.children && item.children.length > 0) {
          exportedItem.children = buildExportOutline(item.children, leafItemsList);
        }
        return exportedItem;
      });
    };

    // 构建合并目录：商务部分在前，技术部分在后
    const bizItems = buildExportOutline(bizOutlineData.outline, bizLeafItems);
    const techItems = buildExportOutline(outlineData.outline, leafItems);

    const mergedOutline: OutlineItem[] = [
      {
        id: 'part-1-biz',
        title: '第一部分 商务部分',
        description: '商务部分',
        children: bizItems,
      },
      {
        id: 'part-2-tech',
        title: '第二部分 技术部分',
        description: '技术部分',
        children: techItems,
      },
    ];

    const payload: any = {
      project_name: projectName || bizOutlineData.project_name || '投标文件',
      outline: mergedOutline,
      generation_mode: 'full',
    };
    if (companyName) {
      payload.company_name = companyName;
    }
    // 传递投标文件格式要求，用于封面格式参考
    if (analysisResults['bid_document_format']) {
      payload.bid_document_format = analysisResults['bid_document_format'];
    }
    return payload;
  }, [outlineData, bizOutlineData, projectName, companyName, leafItems, bizLeafItems, analysisResults]);

  // 导出完整 Word（商务+技术） - 弹出模板选择对话框
  const handleExportFullWord = () => {
    const exportPayload = buildFullExportPayload();
    if (!exportPayload) return;
    fullExportModeRef.current = true;
    setShowExportDialog(true);
  };

  // 模板选择确认后提交导出
  const handleExportConfirm = async (templateId: string) => {
    if (!hasFeature(license?.features, 'export_word')) {
      setShowExportDialog(false);
      setMessage({ type: 'error', text: '未授权「Word 导出」功能，请联系管理员' });
      return;
    }
    setShowExportDialog(false);
    const isFullExport = fullExportModeRef.current;
    const exportPayload = isFullExport ? buildFullExportPayload() : buildExportPayload();
    if (!exportPayload) return;

    try {
      setMessage(null);

      // 提交后台导出任务（携带模板ID）
      await documentApi.exportWordBatch({ ...exportPayload, template_id: templateId });
      const label = isFullExport ? '完整 Word（商务+技术）' : 'Word';
      setMessage({ type: 'success', text: `${label} 导出任务已提交，请在任务列表中查看进度和下载` });
      
      // 刷新任务列表
      fetchTaskList();
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '提交导出任务失败，请重试') });
    }
  };

  // 获取任务列表
  const fetchTaskList = useCallback(async () => {
    try {
      const res = await documentApi.listExportTasks();
      setTaskList(res.data.tasks);
    } catch (err) {
      console.warn('获取任务列表失败:', err);
    }
  }, []);

  // 下载已完成的任务文件
  const handleTaskDownload = async (task: ExportTaskInfo) => {
    try {
      const downloadUrl = documentApi.getExportDownloadUrl(task.task_id);
      const token = localStorage.getItem('auth_token');
      const downloadResponse = await fetch(downloadUrl, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      if (downloadResponse.ok) {
        const blob = await downloadResponse.blob();
        saveAs(blob, task.filename || '标书文档.docx');
        setMessage({ type: 'success', text: `"${task.filename}" 下载成功` });
        // 刷新列表更新 downloaded 状态
        fetchTaskList();
      } else {
        setMessage({ type: 'error', text: '导出文件下载失败，请重试' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: '下载失败，请重试' });
    }
  };

  // 删除任务
  const handleTaskDelete = async (taskId: number) => {
    try {
      await documentApi.deleteExportTask(taskId);
      setTaskList(prev => prev.filter(t => t.task_id !== taskId));
    } catch (err) {
      console.warn('删除任务失败:', err);
    }
  };

  // 管理任务列表轮询（始终轮询以支持按钮上的红色提醒徽章）
  useEffect(() => {
    // 立即获取一次
    fetchTaskList();
    
    if (taskListPollingRef.current) {
      clearInterval(taskListPollingRef.current);
    }
    
    // 面板打开时每 2 秒刷新，关闭时每 10 秒刷新
    const interval = showTaskList ? 2000 : 10000;
    taskListPollingRef.current = setInterval(fetchTaskList, interval);
    
    return () => {
      if (taskListPollingRef.current) {
        clearInterval(taskListPollingRef.current);
        taskListPollingRef.current = null;
      }
    };
  }, [fetchTaskList, showTaskList, taskListPollingRef]);

  // 格式化时间（接收 ISO 字符串，统一北京时间）
  const formatTaskTime = (isoString: string | null): string => fmtDateTimeCST(isoString);

  // 格式化文件大小
  const formatFileSize = (bytes: number): string => {
    if (!bytes || bytes <= 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  // 导出Markdown文档
  const handleExportMarkdown = async () => {
    const exportPayload = buildExportPayload();
    if (!exportPayload) return;

    // 弹出自定义确认对话框，询问是否包含提示词
    setShowPromptDialog(true);
  };

  // 确认导出（含/不含提示词）
  const doExportMarkdown = async (includePrompts: boolean) => {
    const exportPayload = buildExportPayload();
    if (!exportPayload) return;

    setShowPromptDialog(false);

    try {
      setMessage(null);
      const response = await documentApi.exportMarkdown({
        ...exportPayload,
        include_prompts: includePrompts,
        project_overview: includePrompts ? (effectiveOutlineData?.project_overview || '') : undefined,
      });
      const blob = await response.blob();
      saveAs(blob, `${exportPayload.project_name || '标书文档'}.md`);
      setMessage({ type: 'success', text: 'Markdown 导出成功' });
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '导出Markdown失败，请重试') });
    }
  };

  const completedItems = effectiveLeafItems.filter(item => item.content).length;

  /**
   * 清空所有章节内容
   */
  const handleClearChapterContents = async () => {
    if (!effectiveOutlineData || !projectId) return;
    setShowClearContentDialog(false);
    setClearingContent(true);
    setMessage(null);
    try {
      const clearSection = generationMode === 'full' ? activeSection : 'tech';
      await projectApi.clearChapterContents(projectId, clearSection);

      // 清空当前 section 的 leafItems 本地状态
      const setItems = generationMode === 'full' && activeSection === 'biz'
        ? setBizLeafItems : setLeafItems;
      setItems(prev => prev.map(item => ({ ...item, content: undefined })));

      // 清空 draftStore 缓存
      draftStore.clearAll();

      // 同步清空 App.tsx 中 outlineData 叶节点的 content，
      // 避免组件重新挂载时从 outlineData 中恢复旧内容
      const stripContentFromOutline = (items: OutlineItem[]): OutlineItem[] => {
        return items.map(item => {
          if (item.children && item.children.length > 0) {
            return { ...item, children: stripContentFromOutline(item.children) };
          }
          return { ...item, content: undefined };
        });
      };
      const strippedOutline: OutlineData = {
        ...effectiveOutlineData,
        outline: stripContentFromOutline(effectiveOutlineData.outline),
      };
      if (generationMode === 'full' && activeSection === 'biz') {
        onBizOutlineUpdated?.(strippedOutline);
      } else {
        onOutlineUpdated?.(strippedOutline);
      }

      setMessage({ type: 'success', text: '当前部分章节内容已清空' });
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '清空章节内容失败') });
    } finally {
      setClearingContent(false);
    }
  };

  /**
   * 清空非叶节点下所有子节点的内容
   */
  const handleClearNodeContents = async () => {
    if (!clearNodeTarget || !projectId) return;
    setShowClearNodeContentDialog(false);
    setMessage(null);
    try {
      const section = generationMode === 'full' ? activeSection : 'tech';
      // 收集该节点下所有后代叶子节点 ID
      const descendantLeafIds = collectLeafItems(clearNodeTarget.children || []);
      const ids = descendantLeafIds.map(l => l.id);
      
      // 清空本地状态
      const setItemsFunc = generationMode === 'full' && activeSection === 'biz'
        ? setBizLeafItems : setLeafItems;
      setItemsFunc((prev) =>
        prev.map((item) =>
          ids.includes(item.id) ? { ...item, content: undefined } : item
        )
      );

      // 清空 draftStore 缓存
      ids.forEach((id) => draftStore.removeChapterContent(id));

      // 逐条保存空内容到后端
      await Promise.all(
        ids.map((id) =>
          projectApi.saveChapterContent(projectId, id, '', section).catch(() => {})
        )
      );

      setMessage({
        type: 'success',
        text: `已清空「${clearNodeTarget.title}」下 ${ids.length} 个子章节的内容`,
      });
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '清空子节点内容失败') });
    } finally {
      setClearNodeTarget(null);
    }
  };







  if (!effectiveOutlineData) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-center py-12">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">暂无内容</h3>
            <p className="mt-1 text-sm text-gray-500">
              请先在"目录编辑"步骤中生成目录结构
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 max-w-[1400px] mx-auto h-full">
      {/* 只读提示横幅 */}
      {readOnly && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3 flex-shrink-0">
          <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m0-10v4m0 0a2 2 0 100-4 2 2 0 000 4z" />
          </svg>
          <span className="text-sm text-amber-700">此项目为只读共享，内容编辑和生成操作已禁用。</span>
        </div>
      )}
      <div className="flex gap-6 flex-1 min-h-0">
      {/* 左侧目录树侧边栏 */}
      <div
        className={`${showOutlineSidebar ? '' : 'w-0'} flex-shrink-0 overflow-hidden relative`}
        style={showOutlineSidebar ? { width: sidebarWidth, transition: isResizingSidebar ? 'none' : 'all 300ms' } : undefined}
      >
        <div className="bg-white rounded-lg shadow h-full flex flex-col">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">目录结构</h3>
              {outlineSource && (
                <p className="text-[10px] text-gray-400 mt-0.5">
                  {OUTLINE_SOURCE_LABELS[outlineSource]}
                </p>
              )}
            </div>
            <button
              onClick={() => setShowOutlineSidebar(false)}
              className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              title="收起侧边栏"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          </div>
          <div ref={sidebarScrollRef} className="flex-1 overflow-y-auto p-2 space-y-0.5">
            {effectiveOutlineData && renderOutlineTree(effectiveOutlineData.outline)}
          </div>
          {/* 底部操作按钮 */}
          <div className="px-3 py-2 border-t border-gray-200 flex gap-2">
            {outlineData && projectId && !readOnly && (
              <button
                onClick={() => setShowOutlineEditor(true)}
                className="flex-1 inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded border border-blue-200 text-blue-600 hover:bg-blue-50 transition-colors"
              >
                <ArrowsPointingOutIcon className="w-3 h-3 mr-1" />
                全屏编辑
              </button>
            )}
            {outlineData && projectId && !readOnly && (
              <button
                onClick={() => setShowClearContentDialog(true)}
                disabled={clearingContent}
                className="flex-1 inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {clearingContent ? (
                  <>
                    <div className="w-3 h-3 border-2 border-red-400 border-t-transparent rounded-full animate-spin mr-1.5" />
                    清空中...
                  </>
                ) : (
                  <>
                    <TrashIcon className="w-3 h-3 mr-1" />
                    清空章节内容
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 目录结构侧边栏拖拽调整宽度手柄 */}
      {showOutlineSidebar && (
        <div
          onMouseDown={handleResizeMouseDown}
          className="flex-shrink-0 w-1.5 -ml-1.5 cursor-col-resize hover:bg-blue-400/50 active:bg-blue-500/60 rounded transition-colors z-10"
          title="拖动调整面板宽度"
        />
      )}

      {/* 侧边栏收起时的展开按钮 */}
      {!showOutlineSidebar && (
        <button
          onClick={() => setShowOutlineSidebar(true)}
          className="flex-shrink-0 mt-2 p-1.5 rounded-r-lg bg-white shadow border border-l-0 border-gray-200 text-gray-400 hover:text-gray-600 self-start"
          title="展开目录"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {/* 右侧主内容区 - 整体不滚动，内部 prose 容器滚动 */}
      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
        {/* 整套投标文件模式：商务部分/技术部分 切换 tabs */}
        {generationMode === 'full' && (
          <div className="bg-white rounded-lg shadow mb-3 flex-shrink-0">
            <div className="px-4 py-2 flex items-center">
              <div className="flex bg-gray-100 rounded-lg p-0.5">
                <button
                  onClick={() => setActiveSection('tech')}
                  disabled={isGenerating}
                  title={isGenerating ? '生成中，请等待完成后再切换' : undefined}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    activeSection === 'tech'
                      ? 'bg-white text-blue-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  第二部分 技术部分
                </button>
                <button
                  onClick={() => setActiveSection('biz')}
                  disabled={isGenerating || !bizOutlineData || (bizOutlineData.outline?.length ?? 0) === 0}
                  title={isGenerating ? '生成中，请等待完成后再切换' : undefined}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    activeSection === 'biz'
                      ? 'bg-white text-orange-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  第一部分 商务部分
                </button>
              </div>
              {activeSection === 'biz' && (!bizOutlineData || (bizOutlineData.outline?.length ?? 0) === 0) && (
                <span className="ml-3 text-xs text-gray-400">请先在目录编辑步骤中生成商务部分目录</span>
              )}
            </div>
          </div>
        )}

        {/* 顶部工具栏 */}
        <div className="bg-white rounded-lg shadow mb-6 flex-shrink-0">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">标书内容</h2>
                <p className="text-sm text-gray-500 mt-1">共 {effectiveLeafItems.length} 个章节</p>
              </div>
              
              <div className="flex items-center space-x-3">
                {/* 生成标书按钮 / 终止按钮 */}
                {readOnly ? (
                  <button
                    disabled
                    title="只读模式，无法生成标书"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gray-400 cursor-not-allowed"
                  >
                    <PlayIcon className="w-4 h-4 mr-2" />
                    生成标书
                  </button>
                ) : isGenerating ? (
                  <button
                    onClick={handleCancelGeneration}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                  >
                    <XMarkIcon className="w-4 h-4 mr-2" />
                    终止生成
                  </button>
                ) : (
                  /* 生成标书按钮 + 模式选择下拉 */
                  <div className="relative flex">
                    <button
                      onClick={handleGenerateContent}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-l-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <PlayIcon className="w-4 h-4 mr-2" />
                      生成标书
                    </button>
                    <button
                      onClick={() => setShowModeSelect(!showModeSelect)}
                      className="inline-flex items-center px-2 py-2 border border-transparent border-l border-blue-400 text-sm font-medium rounded-r-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      title="切换生成模式"
                    >
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                    {showModeSelect && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setShowModeSelect(false)} />
                        <div className="absolute top-full mt-1 right-0 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-20 py-1">
                          <button
                            onClick={() => { setGenerateMode('foreground'); setShowModeSelect(false); }}
                            className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${generateMode === 'foreground' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}
                          >
                            <span className="flex-1">前台生成（实时预览）</span>
                            {generateMode === 'foreground' && <CheckCircleIcon className="w-4 h-4 text-blue-600" />}
                          </button>
                          <button
                            onClick={() => { setGenerateMode('background'); setShowModeSelect(false); }}
                            className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${generateMode === 'background' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}
                          >
                            <span className="flex-1">后台生成（可关闭页面）</span>
                            {generateMode === 'background' && <CheckCircleIcon className="w-4 h-4 text-blue-600" />}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* 后台任务进度提示 */}
                {bgTaskId && (
                  <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                    后台任务 #{bgTaskId} 运行中...
                  </span>
                )}
                {!readOnly && (
                <button
                  onClick={() => setShowVersionManager(true)}
                  className="inline-flex items-center px-4 py-2 border border-purple-300 text-sm font-medium rounded-md text-purple-700 bg-purple-50 hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  <ArchiveBoxIcon className="w-4 h-4 mr-2" />
                  版本管理
                </button>
                )}
                
                {/* 导出下拉菜单 */}
                <div className="relative">
                  <button
                    onClick={() => setShowExportMenu(!showExportMenu)}
                    disabled={isGenerating}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
                    导出
                    <ChevronDownIcon className="w-3.5 h-3.5 ml-1.5" />
                  </button>
                  {showExportMenu && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowExportMenu(false)} />
                      <div className="absolute top-full mt-1 right-0 w-44 bg-white border border-gray-200 rounded-md shadow-lg z-20 py-1">
                        <button
                          onClick={() => { setShowExportMenu(false); handleExportWord(); }}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        >
                          <DocumentArrowDownIcon className="w-4 h-4" />
                          导出 Word
                        </button>
                        <button
                          onClick={() => { setShowExportMenu(false); handleExportMarkdown(); }}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        >
                          <CodeBracketIcon className="w-4 h-4" />
                          导出 Markdown
                        </button>
                        {generationMode === 'full' && (
                          <button
                            onClick={() => { setShowExportMenu(false); handleExportFullWord(); }}
                            className="w-full text-left px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 flex items-center gap-2 border-t border-gray-100"
                          >
                            <DocumentArrowDownIcon className="w-4 h-4" />
                            导出完整 Word（商务、技术）
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* 任务列表按钮 */}
                <button
                  onClick={() => setShowTaskList(!showTaskList)}
                  className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                    showTaskList 
                      ? 'border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100' 
                      : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                  }`}
                >
                  <QueueListIcon className="w-4 h-4 mr-2" />
                  任务列表
                  {/* 已完成且未下载的任务用红色提醒 */}
                  {(() => {
                    const newCompleted = taskList.filter(t => t.status === 'completed' && !t.downloaded).length;
                    const running = taskList.filter(t => t.status === 'running').length;
                    if (newCompleted > 0) {
                      return (
                        <span className="ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 text-xs font-bold text-white bg-red-500 rounded-full px-1 animate-pulse">
                          {newCompleted}
                        </span>
                      );
                    }
                    if (running > 0) {
                      return (
                        <span className="ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 text-xs font-bold text-white bg-blue-500 rounded-full px-1">
                          {running}
                        </span>
                      );
                    }
                    return null;
                  })()}
                </button>
              </div>
            </div>
            
            {/* 进度条 */}
            {isGenerating && (
              <div className="flex items-center justify-between text-sm text-gray-600 mb-2 mt-4">
                <div className="flex items-center space-x-4">
                  <span>正在生成: {progress.current}</span>
                  <span>{progress.completed} / {progress.total}</span>
                </div>
                <div className="flex-1 mx-4">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(progress.completed / progress.total) * 100}%` }}
                    />
                  </div>
                </div>
                {message && (
                  <div className={`rounded-md border px-3 py-1 text-xs whitespace-nowrap ${
                    message.type === 'success'
                      ? 'border-green-200 bg-green-50 text-green-700'
                      : 'border-red-200 bg-red-50 text-red-700'
                  }`}>
                    {message.text}
                  </div>
                )}
              </div>
            )}

            {message && !isGenerating && (
              <div className={`mt-4 rounded-md border px-4 py-3 text-sm ${
                message.type === 'success'
                  ? 'border-green-200 bg-green-50 text-green-700'
                  : 'border-red-200 bg-red-50 text-red-700'
              }`}>
                {message.text}
              </div>
            )}
          </div>
        </div>

        {/* 文档内容 - 可滚动区域 */}
        <div ref={contentScrollRef} className="flex-1 min-h-0 overflow-y-auto bg-white rounded-lg shadow">
          <div className="p-8">
            <div className="prose max-w-none">
              {/* 文档标题 */}
              <h1 className="text-3xl font-bold text-gray-900 mb-8">
                {generationMode === 'full' && activeSection === 'biz'
                  ? `${effectiveOutlineData?.project_name || projectName || '投标文件'} - 第一部分 商务部分`
                  : generationMode === 'full'
                    ? `${effectiveOutlineData?.project_name || projectName || '投标文件'} - 第二部分 技术部分`
                    : effectiveOutlineData?.project_name || projectName || '投标技术文件'}
              </h1>
              
              {/* 项目概述 */}
              {effectiveOutlineData?.project_overview && (
                <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-8">
                  <h2 className="text-lg font-semibold text-blue-900 mb-2">项目概述</h2>
                  <p className="text-blue-800">{effectiveOutlineData.project_overview}</p>
                </div>
              )}

              {/* 目录结构和内容 */}
              {effectiveOutlineData && (
                <div className="space-y-8">
                  {renderOutline(effectiveOutlineData.outline)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 底部统计 */}
        <div className="mt-6 bg-white rounded-lg shadow p-4 flex-shrink-0">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <div className="flex items-center space-x-6">
              <div className="flex items-center">
                <CheckCircleIcon className="w-4 h-4 text-green-500 mr-1" />
                <span>已完成: {completedItems}</span>
              </div>
              <div className="flex items-center">
                <DocumentTextIcon className="w-4 h-4 text-gray-400 mr-1" />
                <span>待生成: {effectiveLeafItems.length - completedItems}</span>
              </div>
              {progress.failed.length > 0 && (
                <div className="flex items-center space-x-2">
                  <div className="flex items-center">
                    <ExclamationCircleIcon className="w-4 h-4 text-red-500 mr-1" />
                    <span className="text-red-600">失败: {progress.failed.length}</span>
                  </div>
                  <button
                    onClick={handleRetryFailed}
                    disabled={isGenerating || readOnly}
                    className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={readOnly ? '只读模式，无法重新生成' : '重新生成失败的章节'}
                  >
                    <ArrowPathIcon className="w-3 h-3 mr-1" />
                    重新生成
                  </button>
                </div>
              )}
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={imageCheckHook.checkAllImages}
                disabled={imageCheckHook.checkingImages}
                className="inline-flex items-center text-xs text-gray-500 hover:text-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="检查正文中插入的图片是否有效"
              >
                {imageCheckHook.checkingImages ? (
                  <svg className="animate-spin w-4 h-4 mr-1" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <PhotoIcon className="w-4 h-4 mr-1" />
                )}
                检查插入图片
              </button>
              {imageCheckHook.imageResults !== null && (
                <span
                  onClick={() => imageCheckHook.setShowImageCheck(true)}
                  className={`cursor-pointer text-xs font-medium px-2 py-0.5 rounded ${
                    imageCheckHook.imageResults.length === 0
                      ? 'text-green-600 bg-green-50'
                      : 'text-red-600 bg-red-50 hover:bg-red-100'
                  }`}
                >
                  {imageCheckHook.imageResults.length === 0 ? '✓ 无无效图片' : `✗ ${imageCheckHook.imageResults.length} 个章节有无效图片`}
                </span>
              )}
              <button
                onClick={mermaidHook.checkMermaidErrors}
                disabled={mermaidHook.checkingMermaid}
                className="inline-flex items-center text-xs text-gray-500 hover:text-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="检查正文中的 mermaid 图表错误"
              >
                {mermaidHook.checkingMermaid ? (
                  <svg className="animate-spin w-4 h-4 mr-1" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <ExclamationCircleIcon className="w-4 h-4 mr-1" />
                )}
                检查 mermaid
              </button>
              {mermaidHook.mermaidErrors !== null && (
                <span
                  onClick={() => mermaidHook.setShowMermaidErrors(true)}
                  className={`cursor-pointer text-xs font-medium px-2 py-0.5 rounded ${
                    mermaidHook.mermaidErrors.length === 0
                      ? 'text-green-600 bg-green-50'
                      : 'text-red-600 bg-red-50 hover:bg-red-100'
                  }`}
                >
                  {mermaidHook.mermaidErrors.length === 0 ? '✓ 无错误' : `✗ ${mermaidHook.mermaidErrors.length} 个章节有错误`}
                </span>
              )}
              <span>总字数: {effectiveLeafItems.reduce((sum, item) => sum + (item.content?.length || 0), 0)}</span>
            </div>
          </div>
        </div>

        {/* Mermaid 错误弹窗 */}
        <ContentEditMermaidPanel
          show={mermaidHook.showMermaidErrors && mermaidHook.mermaidErrors !== null}
          errors={mermaidHook.mermaidErrors}
          fixingIds={mermaidHook.fixingMermaidIds}
          fixedIds={mermaidHook.fixedMermaidIds}
          expandedErrors={mermaidHook.expandedErrors}
          fixingAll={mermaidHook.fixingAllMermaid}
          fixProgress={mermaidHook.fixMermaidProgress}
          onClose={() => mermaidHook.setShowMermaidErrors(false)}
          onEditChapter={(item) => {
            if (readOnly) return;
            mermaidHook.setShowMermaidErrors(false);
            startEditContent(item);
          }}
          onFixChapter={mermaidHook.fixMermaidChapter}
          onFixAll={mermaidHook.fixAllMermaid}
          readOnly={readOnly}
          onToggleExpandError={(key) => {
            mermaidHook.setExpandedErrors(prev => {
              const next = new Set(prev);
              if (next.has(key)) next.delete(key);
              else next.add(key);
              return next;
            });
          }}
        />

        {/* 图片检查弹窗 */}
        <ContentEditImageCheckPanel
          show={imageCheckHook.showImageCheck && imageCheckHook.imageResults !== null}
          results={imageCheckHook.imageResults}
          clearingIds={imageCheckHook.clearingIds}
          clearingAll={imageCheckHook.clearingAll}
          onClose={() => imageCheckHook.setShowImageCheck(false)}
          onClearChapter={imageCheckHook.clearChapterImages}
          onClearAll={imageCheckHook.clearAllBrokenImages}
        />


        {/* 回到顶部按钮 */}
        {showScrollToTop && (
          <button
            onClick={scrollToTop}
            className="fixed bottom-24 right-6 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-3 shadow-lg transition-all duration-300 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 z-[60]"
            aria-label="回到顶部"
          >
            <ArrowUpIcon className="w-5 h-5" />
          </button>
        )}

        {/* 导出提示词确认对话框 */}
        <ConfirmDialog
          open={showPromptDialog}
          title="是否导出提示词？"
          confirmText="否"
          cancelText="是"
          onConfirm={() => doExportMarkdown(false)}
          onCancel={() => doExportMarkdown(true)}
        >
          <p className="mb-2">
            导出的 Markdown 文件将包含每个章节的提示词上下文信息：
          </p>
          <ul className="list-disc pl-5 space-y-1 text-gray-500">
            <li>章节ID、标题、描述</li>
            <li>字数范围、来源需求信息</li>
            <li>项目概述</li>
          </ul>
          <p className="mt-2 text-gray-400">
            选择"否"则仅导出正文内容，不包含提示词。
          </p>
        </ConfirmDialog>

        {/* 清空章节内容确认对话框 */}
        <ConfirmDialog
          open={showClearContentDialog}
          title="确认清空章节内容？"
          confirmText="确认"
          cancelText="取消"
          confirmClassName="bg-red-600 hover:bg-red-700"
          onConfirm={handleClearChapterContents}
          onCancel={() => setShowClearContentDialog(false)}
        >
          <p className="text-gray-600">
            此操作将清空所有章节已生成的内容，但保留目录结构。
          </p>
          <p className="mt-2 text-red-500 font-medium">
            此操作不可撤销，请确认是否继续？
          </p>
        </ConfirmDialog>

        {/* 清空子节点内容确认对话框 */}
        <ConfirmDialog
          open={showClearNodeContentDialog}
          title="确认清空子节点内容？"
          confirmText="确认"
          cancelText="取消"
          confirmClassName="bg-red-600 hover:bg-red-700"
          onConfirm={handleClearNodeContents}
          onCancel={() => { setShowClearNodeContentDialog(false); setClearNodeTarget(null); }}
        >
          <p className="text-gray-600">
            此操作将清空「{clearNodeTarget?.title || ''}」下所有子章节已生成的内容，但保留目录结构。
          </p>
          <p className="mt-2 text-red-500 font-medium">
            此操作不可撤销，请确认是否继续？
          </p>
        </ConfirmDialog>

        {/* Word 导出模板选择对话框 */}
        <WordExportDialog
          open={showExportDialog}
          onClose={() => setShowExportDialog(false)}
          onConfirm={handleExportConfirm}
        />

        {/* 目录结构全屏编辑器 */}
        {effectiveOutlineData && (
          <OutlineEditorDialog
            open={showOutlineEditor}
            outlineData={effectiveOutlineData}
            projectId={projectId}
            section={generationMode === 'full' && activeSection === 'biz' ? 'biz' : 'tech'}
            onClose={() => setShowOutlineEditor(false)}
            onOutlineUpdated={generationMode === 'full' && activeSection === 'biz'
              ? (data) => onBizOutlineUpdated?.(data) 
              : onOutlineUpdated
            }
            readOnly={readOnly}
          />
        )}

        {/* 版本管理弹窗 */}
        {projectId && (
          <VersionManager
            projectId={projectId}
            isOpen={showVersionManager}
            onClose={() => setShowVersionManager(false)}
            onRestore={handleVersionRestore}
            currentTokens={currentTokens}
            currentChapters={currentGeneratedChapters}
            activeSection={generationMode === 'full' ? activeSection : undefined}
            currentChapterContents={currentChapterContentsForDiff}
            currentOutlineNodes={currentOutlineNodesForDiff}
            onUpdateCurrent={handleDiffUpdateCurrent}
          />
        )}

                {/* 任务列表面板 */}
        <ContentEditTaskPanel
          show={showTaskList}
          taskList={taskList}
          isAdmin={isAdmin}
          currentUserId={user?.id}
          onClose={() => setShowTaskList(false)}
          onDownload={handleTaskDownload}
          onDelete={handleTaskDelete}
        />


        {/* 全屏 Markdown 编辑器 */}
        <FullscreenEditor
          open={fullscreenEditorItem !== null}
          title={fullscreenEditorItem ? `${fullscreenEditorItem.id} ${fullscreenEditorItem.title}` : undefined}
          initialContent={fullscreenEditorItem?.content || ''}
          onSave={handleFullscreenSave}
          onClose={() => setFullscreenEditorItem(null)}
          mermaidDefaultView={mermaidDefaultView}
          onPrevChapter={goPrevChapter}
          onNextChapter={goNextChapter}
          canPrev={fsCanPrev}
          canNext={fsCanNext}
          chapterLabel={fsChapterLabel}
        />
      </div>
      </div>
    </div>
  );
};

export default ContentEdit;
